import type { ComponentType, ReactNode, RefObject } from 'react';
/* `import type` ET RIEN D'AUTRE : `ScreenLayout.tsx` importe deja ce fichier
   (les types de lignes, de tri et d'etat). Un import de VALEUR fermerait le
   cycle a l'execution ; un import de type est efface a la compilation, et les
   deux modules restent chacun a leur place. */
import type { ScreenAction } from '../components/ScreenLayout.js';
/* Meme regle : la forme de la carte des fichiers joints, declaree a cote des
   autres cartes de fiche. */
import type { CarteFichiers } from './screens.js';
import type { TypeDeChamp } from './fields.js';
import type { Couleur } from '@taki/data-model';

/**
 * LE CONTRAT DES VUES NATIVES DU CORE.
 *
 * C'est le fichier de reference du chantier "vues interchangeables"
 * (docs/VUES-CONTRAT.md, section 3). Tout le reste en decoule.
 *
 * LE MODELE MENTAL, en trois objets separes :
 *
 *   1. L'ECRAN declare CE QUE SONT ses donnees, jamais comment les dessiner :
 *      une REQUETE (`ScreenQuery` : recherche, filtre, tri, fenetre) et des
 *      BINDINGS (`ViewBinding` : ses champs metier projetes sur des ROLES
 *      d'affichage, plus ses colonnes).
 *   2. Le CORE projette : chaque enregistrement brut devient une ligne neutre
 *      (`ProjectedRow`), forme UNIQUE que toutes les vues recoivent. Le core
 *      tient aussi l'etat partage (vue active, selection, curseur de fiche).
 *   3. La VUE ne fait que RENDRE. Elle recoit UNIQUEMENT des props
 *      (`ViewProps`). Elle ne lit aucun contexte React, ne recoit jamais le
 *      SDK, ne va jamais chercher ses donnees, ne navigue jamais elle-meme.
 *      C'EST LA SEULE REGLE ABSOLUE : c'est elle qui permet a la meme vue de
 *      servir dans 40 apps et de remonter plus tard en widget de dashboard.
 *
 * Deux NIVEAUX de vue : `collection` (liste, tableau, kanban, calendrier) et
 * `record` (la fiche). Le selecteur de vues (3c) porte les deux, en deux
 * groupes separes par un filet.
 *
 * Une vue declare ce dont elle a BESOIN (`ViewType.requires`). Si l'ecran ne
 * le fournit pas, l'entree du selecteur est GRISEE avec sa raison au survol,
 * jamais absente : le developpeur doit voir ce qu'il rate et ce qu'il doit
 * declarer pour la debloquer.
 *
 * Ce fichier ne contient QUE des types : il ne produit aucun code a
 * l'execution (import type des deux cotes, aucun cycle possible).
 */

/* ------------------------------------------------------------------ */
/* Identite d'une vue                                                  */
/* ------------------------------------------------------------------ */

/**
 * Id d'un type de vue, TOUJOURS namespace : `<espace>.<nom>`.
 * Le core possede l'espace `core` (core.liste, core.fiche, core.tableau,
 * core.kanban, core.calendrier) ; une app custom prend le sien
 * (`hello.carte`), sans aucun privilege en moins ni en plus.
 */
export type ViewId = `${string}.${string}`;

/**
 * Niveau d'une vue. `collection` = une lentille sur le jeu d'enregistrements ;
 * `record` = un seul element ouvert (la fiche). Le niveau se voit dans le code
 * ET a l'ecran (second groupe du selecteur).
 */
export type ViewLevel = 'collection' | 'record';

/* ------------------------------------------------------------------ */
/* Roles et valeurs                                                    */
/* ------------------------------------------------------------------ */

/**
 * Les ROLES d'affichage du core : le vocabulaire commun entre un champ metier
 * et une vue. L'ecran projette ses champs dessus, la vue ne connait qu'eux.
 *
 * - `title`     : ce qui identifie la ligne (repli universel, exige partout).
 * - `subtitle`  : la ligne secondaire.
 * - `avatar`    : URL ou initiales pour la pastille de tete de ligne.
 * - `couleur`   : la couleur de la fiche (`Couleur`), en anneau autour de sa
 *                 pastille. Posee par l'assemblage sur les portes dont le jeu
 *                 l'active (`jeux[].couleur`), jamais declaree par une app.
 * - `photo`     : l'id du fichier de la photo de reference de la fiche, meme
 *                 regle (`jeux[].photo`). Elle se lit par le contexte
 *                 `PhotosDeFiches`, elle ne traverse jamais la frontiere en URL.
 * - `status`    : l'etat de l'element, resolu en `Option` (exige `options()`).
 * - `badges`    : les etiquettes secondaires de la ligne (tags), multi-value,
 *                 resolues en `Option` comme le statut ; la liste les rend a
 *                 cote de la pastille de statut (05/08, tags du cockpit).
 * - `groupKey`  : ce qui fait les colonnes du kanban (exige `options()`).
 * - `lane`      : le couloir du calendrier, multi-value possible (exige `options()`).
 * - `dateStart` / `dateEnd` : le placement dans le temps.
 *
 * Une vue custom peut declarer ses propres roles (`ViewType.extraRoles`,
 * ex. 'lat' / 'lng') : ils atterrissent dans `ProjectedRow.extra`.
 */
export type FieldRole =
  | 'title'
  | 'subtitle'
  | 'avatar'
  | 'couleur'
  | 'photo'
  | 'status'
  | 'badges'
  | 'groupKey'
  | 'lane'
  | 'dateStart'
  | 'dateEnd';

/** Valeur brute d'un champ, apres projection. Volontairement pauvre : ce qui traverse la frontiere core / vue. */
export type Cell = string | number | Date | boolean | null;

/**
 * Couleur d'une option, seul vocabulaire de COULEUR partage par les apps.
 *
 * Les mots des statuts appartiennent au metier (donc au tenant), les couleurs
 * appartiennent a la plateforme : le `tone` fait le pont. C'est une `Couleur`
 * (packages/data-model/src/couleurs.ts) : une clef de la grille du design, qui
 * suit le theme (les quatre tons d'avant en tete), ou un hex garde tel quel,
 * choisi dans les reglages. Le rendu passe par `couleurCss` (lib/couleurs.ts),
 * jamais par une table de classes : aucun hex en dur dans une app.
 */
export type Tone = Couleur;

/**
 * Une valeur possible d'un role catalogue (statut, colonne de kanban, couloir).
 * Resolue au RUNTIME par `FieldMap.options()`, donc depuis les reglages du
 * tenant : deux clients de la meme app n'ont pas les memes colonnes.
 */
export interface Option {
  archived?: boolean;
  /** Valeur stockee, telle qu'elle vit dans la donnee. */
  value: string;
  /** Libelle affiche (francais, mot du metier). */
  label: string;
  /** Couleur logique. Voir `Tone`. */
  tone: Tone;
}

/* ------------------------------------------------------------------ */
/* Ce que l'ECRAN declare                                              */
/* ------------------------------------------------------------------ */

/**
 * UNE PARTIE D'UN ROLE : ce qu'un champ lui apporte, et la fiche qu'il designe
 * quand il en designe une. Voir `FieldMap.parts`.
 */
export interface FieldPart<T> {
  /** La valeur de cette partie, telle qu'elle est stockee. */
  get(record: T): Cell;
  /**
   * LA PORTE QUE CETTE PARTIE DESIGNE par l'id qu'elle porte. Deduite, jamais
   * declaree, par la meme regle que `Column.reference`.
   */
  reference?: ColumnReference;
  /**
   * LES ANNUAIRES QUE CETTE PARTIE LIT en plus de sa `reference`, quand c'est un
   * champ `lecture` : un par etage de lien suivi (`Column.annuaires`).
   */
  annuaires?: readonly ColumnReference[];
  /**
   * LE CHEMIN DE STOCKAGE DU CHAMP de cette partie. Un role compose (`{ champ,
   * affichage: "libelle" }`) n'a pas de `path` de role, mais sa partie sait quel
   * champ elle lit : c'est par lui que la projection retrouve le mot du champ
   * (« Article ») d'un titre calcule.
   */
  path?: string;
}

/**
 * Un champ metier projete sur un role : la brique de base du binding.
 *
 * `get` est la seule methode obligatoire. Tout le reste raffine :
 * `render` embellit sans jamais servir au tri ni a l'export, `options()`
 * fournit le catalogue quand le role est un statut / une colonne / un couloir,
 * `set` autorise l'ecriture (sans `set`, pas d'affordance : une carte de
 * kanban n'est simplement pas deplacable).
 */
export interface FieldMap<T> {
  /** La valeur du champ pour cet enregistrement. */
  get(record: T): Cell;
  /** Variante multi-valuee : une reservation qui occupe 3 salles. */
  getMany?(record: T): Cell[];
  /** Affichage seul (jamais trie, jamais exporte). */
  render?(record: T): ReactNode;
  /** Libelle du role, quand une vue a besoin de le nommer. */
  label?: string;
  /** Texte quand la valeur est vide ("Jamais", "Non renseigne"), rendu en --ink-300. */
  empty?: string;
  /**
   * LES PARTIES DONT CE ROLE EST FAIT, quand il en a qui DESIGNENT UNE FICHE.
   *
   * Un sous-titre « Client » stocke l'id d'un contact, jamais son nom (un lien
   * stocke un id, `ColumnReference`). Une colonne le sait par `reference`, et
   * la projection en fait un nom par l'annuaire ; un role ne le savait pas, et
   * la liste affichait `tacs-hotel-bains` la ou le tableau, une vue plus loin,
   * disait « TACS Hotel des Bains ». C'est ce que ceci ferme : `texteDuRole`
   * (`views/project.ts`) lit ces parties et NOMME celles qui portent un id.
   *
   * DES PARTIES, ET PAS UNE SEULE REFERENCE, parce que le sous-titre est le
   * seul role dont un COMPOSE est la forme courante (le Studio le coche champ
   * par champ) : « Client · Echeance » doit nommer le client et garder la date.
   * Un role d'un seul champ est un compose d'une partie.
   *
   * `get` RESTE LA VALEUR DU ROLE, et il en est DERIVE a l'assemblage
   * (`views/screens.ts`, `roleDe`) : les parties sont ecrites une fois, la
   * valeur jointe en decoule, et rien ne peut diverger. Absent chez un binding
   * ecrit a la main : le role garde exactement son comportement, la valeur
   * brute.
   */
  parts?: readonly FieldPart<T>[];
  /** La meme composition apres resolution des references par la projection. */
  compose?(record: T, nommer: (part: FieldPart<T>) => Cell): Cell;
  /**
   * LE CHEMIN DE STOCKAGE DU ROLE, quand sa source est un champ unique
   * (`name`, `status`, `customFields.objet`). Meme sens que `Column.path`, et
   * pose par le meme assemblage (`views/screens.ts`, `cheminDeStockage`).
   *
   * A QUOI IL SERT, ET A RIEN D'AUTRE AUJOURD'HUI : la fiche de CREATION range
   * les saisies dans une fiche qui n'existe pas encore, et le nom du ROLE
   * (`title`, `subtitle`) n'est pas le nom du champ. Sans ce chemin, le titre
   * saisi partirait au serveur sous la clef `title`, que la famille ne porte
   * pas : la fiche se creerait sans nom, sans une seule erreur.
   *
   * ABSENT = le role est compose, calcule, ou l'app l'a ecrit a la main : le
   * moteur retombe sur le nom du role.
   */
  path?: string;
  /**
   * LE ROLE EST UN INSTANT, a lire dans CE fuseau (nom IANA, celui de l'org).
   * Pose a l'assemblage quand la source du role est UN SEUL champ DECLARE de
   * type `datetime` (`views/screens.ts`, `roleDe`) ; un binding ecrit a la main
   * peut le poser lui-meme, c'est ce qui rend la capacite generique.
   *
   * C'EST LE TYPE DECLARE QUI DIT « INSTANT », JAMAIS LA FORME DE LA VALEUR :
   * l'app Taches stocke `socle:dueAt` en `Date` a midi local mais le declare
   * `date`, et ses cartes ne doivent pas se mettre a ecrire « 12:00 ».
   *
   * Sa seule lecture aujourd'hui : la projection pose `ProjectedRow.startFuseau`
   * (`views/project.ts`), et la carte de kanban ecrit l'heure
   * (`texteDHeureDeCarte`, `views/champs-types.ts`). Absent = une date, comme avant.
   */
  instant?: { fuseau: string };
  /**
   * LE PLACEHOLDER DE LA CASE, quand elle est VIDE et qu'on peut la remplir
   * (« Sony FX9 n°3 »). Lu sur le role `title` d'un brouillon de creation, et
   * tolere ailleurs : c'est la fiche qui decide ou elle le rend.
   *
   * IL NE REMPLACE PAS `empty` : `empty` dit ce qu'une fiche EXISTANTE n'a pas
   * (« Non renseigne »), le placeholder invite a ecrire ce qui n'existe pas
   * encore. Les deux ne parlent jamais au meme moment.
   */
  placeholder?: string;
  /**
   * Catalogue des valeurs possibles, resolu au RUNTIME (reglages du tenant).
   * OBLIGATOIRE pour `status`, `groupKey` et `lane` : sans lui, le kanban
   * fabrique des colonnes fantomes (verifie par `assertBinding`).
   */
  options?(): Option[];
  /**
   * Ecriture du champ. Sa PRESENCE est ce qui rend le role editable
   * (`ProjectedRow.editable`). L'ecriture ne quitte jamais le core : la vue
   * appelle `onPatchRecord`, le core applique l'optimisme puis appelle ceci.
   */
  set?(record: T, value: Cell): Promise<void>;
}

/**
 * Le TYPE D'EDITEUR d'un champ editable en fiche.
 *
 * Union litterale et non `string` : la fiche fait un `switch` dessus, et une
 * valeur inconnue doit se voir a la compilation, pas au clic. ATTENTION au
 * vocabulaire : `TableView` porte un type LOCAL `ColumnKind`
 * ('title' | 'status' | 'cell') qui decrit la NATURE d'une colonne de rendu, pas
 * son editeur. Les deux ne se croisent jamais, mais ils se ressemblent assez
 * pour se confondre a la relecture.
 *
 * `lien` EDITE UN ID PAR UN CHOIX DANS L'ANNUAIRE DE LA PORTE REFERENCEE : la
 * case porte le TITRE d'une fiche d'un autre jeu, la valeur ecrite reste son
 * ID. Il ne se declare dans aucun fichier (`EDITEUR_PAR_TYPE`, `views/fields.ts`,
 * ne le rend jamais) : c'est l'ASSEMBLAGE qui le pose, sur une colonne dont un
 * lien entrant fait une REFERENCE (`ColumnReference`) et qui porte un `set`.
 * Une reference en lecture seule reste un `text` : il n'y a rien a choisir.
 *
 * `multiselect` COCHE PLUSIEURS VALEURS D'UN CATALOGUE (les jours d'un plan
 * recurrent). DANS L'ESPACE DE L'EDITEUR, SA VALEUR EST UNE CELLULE ORDINAIRE :
 * les clefs cochees, UNE PAR LIGNE, exactement la forme sous laquelle la
 * lecture rend deja une liste (`cellule`, `app-runtime/src/acces.ts`). C'est la
 * COLONNE qui la retraduit en LISTE au moment d'ecrire (`colonnesDe`,
 * `views/fields.ts`) : `Cell` ne porte pas de tableau, et lui en ajouter un
 * ferait passer un type de plus par le tri, l'export et chaque comparaison du
 * moteur, pour un seul editeur.
 *
 * LES EDITEURS DES TYPES DU 27/10/2026 (`docs/socle/champs-types.md`) suivent
 * la meme regle : leur valeur est une CELLULE. `boolean` emet un booleen (un
 * clic sur l'interrupteur), `number` et `note` un nombre, `periode` la chaine
 * `debut/fin`, et les trois editeurs de LISTE (`etiquettes`, `liens`,
 * `checklist`) leurs elements UN PAR LIGNE, comme `multiselect` : c'est la
 * colonne qui ecrit le tableau (`editeurEnListe`, `views/champs-types.ts`).
 * `calcul` et `numero` n'ont pas d'editeur : leur valeur est posee par le socle.
 *
 * UN EDITEUR N'EST PAS UN TYPE DE CHAMP. Un prix, un pourcentage et une duree
 * sont des `number` (`formatDuNombre`, `@taki/data-model`) : ils ont TOUS
 * l'editeur `number`, qui lit le FORMAT dans les reglages de la colonne
 * (`devise`, `affichage`, `habillage`) pour poser son suffixe (« € », « % »),
 * sa saisie (« 1 250,50 € », « 2 h 30 ») ou sa jauge. `liens` est l'editeur
 * d'un `lien` qui porte `plusieurs` : il AJOUTE et RETIRE des fiches, la ou
 * `lien` en choisit UNE, comme `multiselect` a cote de `select`.
 */
export type EditorKind =
  | 'text'
  | 'multiline'
  | 'number'
  | 'date'
  | 'datetime'
  | 'select'
  | 'multiselect'
  | 'lien'
  | 'liens'
  | 'boolean'
  | 'etiquettes'
  | 'periode'
  | 'checklist'
  | 'note';

/**
 * LES REGLAGES D'AFFICHAGE D'UN CHAMP, recopies de sa declaration
 * (`taki.fields.json`) par `colonnesDe` : l'unite d'un nombre (« m² »), la
 * devise d'un montant, les decimales, la jauge d'un pourcentage, les liens
 * multiples, la note maximale, et la formule d'un calcul DEJA RENDUE LISIBLE
 * (`formuleLisible`, « Prime annuelle * 1,2 »). Et L'HABILLAGE que la
 * declaration d'ecran donne a la colonne (`euros`, `pourcentage`, `duree`),
 * quand il donne un FORMAT au nombre (`FORMAT_PAR_HABILLAGE`) : c'est ainsi
 * que l'existant code un prix, un avancement ou un temps alloue.
 *
 * DE LA DONNEE, JAMAIS UNE FONCTION : ils traversent la frontiere de la vue
 * (`ViewColumn.reglages`) pour que l'editeur de la fiche pose son suffixe, sa
 * saisie et sa jauge, que la note sache combien d'etoiles dessiner, et que le
 * pied, le filtre et le PDF lisent le meme format que la case
 * (`formatDuNombre`). Ils ne changent JAMAIS la valeur : le tri, le filtre,
 * l'export et l'agregat portent le nombre nu.
 */
export interface ReglagesDeColonne {
  unite?: string;
  devise?: string;
  decimales?: number;
  /** `jauge` : un `number` de 0 a 100, montre et regle en jauge. */
  affichage?: 'jauge';
  /** Un `lien` qui porte `plusieurs` : une liste d'ids. */
  plusieurs?: boolean;
  max?: number;
  formule?: string;
  /** L'habillage de la colonne qui donne un format au nombre (`euros`, `pourcentage`, `duree`). */
  habillage?: string;
  /** Un `datetime` : le fuseau de l'org (nom IANA) dans lequel l'instant se lit, Europe/Luxembourg sans lui. */
  fuseau?: string;
}

/**
 * UN CHAMP MODIFIABLE EN LOT (chantier 3_0) : une entree du catalogue que le
 * core rend a la fenetre "Modification groupee" (`ScreenState.bulkEditFields`).
 *
 * D'OU IL SORT, et de nulle part ailleurs : une COLONNE dont la cellule porte
 * `editable: true` (l'ecran a declare son `set()`), ou un ROLE dont l'ecran a
 * fourni le `set()`. Le core ne fabrique aucun champ : ce qui ne s'ecrit pas
 * une fiche a la fois ne s'ecrit pas non plus douze d'un coup.
 *
 * LISTE VIDE = aucun champ de la vue active ne s'ecrit. C'est un fait a DIRE
 * (bouton grise avec sa raison, fenetre qui l'annonce), jamais une fenetre
 * qu'on ouvre vide.
 */
export interface BulkEditField {
  /**
   * La cle, a RENDRE TELLE QUELLE a `ScreenState.writeCell`. Elle est OPAQUE :
   * elle porte l'espace de cles (`role:<nom>` ou `col:<cle>`), parce que les
   * deux espaces peuvent collisionner (une colonne `status` n'est PAS le role
   * `status`) et que le core ne les melange jamais. C'est la forme que la fiche
   * emploie deja pour designer une case, et c'est LA MEME FABRIQUE, pas la meme
   * forme : `fieldKeyOf`, dans `views/core/CellEditor.js`. Une seule convention
   * dans le paquet, tenue par le code et non par un commentaire.
   */
  key: string;
  /** Le nom du champ, tel que le tableau l'affiche en tete de colonne. */
  label: string;
  /** L'editeur a monter pour saisir la valeur. */
  editor: EditorKind;
  /**
   * LE LIBELLE DE L'OPTION, type d'editeur COMPRIS ("Avancement, liste de
   * choix"). Le type s'annonce DANS l'option et jamais dans une colonne a
   * part : on sait ce qu'on va devoir saisir avant de s'engager, et la liste
   * deroulante reste un champ comme les autres.
   */
  editorLabel: string;
  /** Le catalogue du tenant, pour `editor: 'select'` UNIQUEMENT. */
  options?: Option[];
  /** Les reglages d'affichage du champ (l'unite, la devise, la note maximale). */
  reglages?: ReglagesDeColonne;
}

/**
 * LA PORTE QU'UNE COLONNE DESIGNE par l'id qu'elle porte, et de quoi lire le
 * titre d'une fiche de cette porte.
 *
 * D'OU ELLE SORT : elle ne se declare JAMAIS, elle se DEDUIT du sens inverse
 * d'un lien. Une porte parente declare `liens: [{ id, porte, par }]` ; a
 * l'assemblage de la porte LIEE, la colonne dont le chemin de stockage est ce
 * `par` recoit cette reference (`views/screens.ts`). Ecrire la relation deux
 * fois, une par sens, la ferait diverger au premier renommage.
 *
 * `titleOf` PREND UNE FICHE BRUTE, pas un `T` : les fiches d'un annuaire
 * arrivent du port de l'hote (`LiensPort.list`), qui rend des documents, comme
 * pour une carte liee. Le titre est celui du role `title` du binding
 * `core.liste` de la porte referencee : un seul titre par porte, celui que ses
 * propres listes affichent.
 */
export interface ColumnReference {
  /**
   * L'id de la porte referencee, dans le meme ecran, QUAND IL EN EXISTE UNE.
   *
   * ABSENT = la colonne designe une famille que cet ecran ne monte pas : une
   * reference DECLAREE (`type: lien`), pas le sens inverse d'un lien. Le nom
   * s'affiche quand meme (l'annuaire se retrouve par `family`/`type`) ; ce qui
   * change, c'est ou la fleche mene : dans une autre app, par le shell
   * (`ViewProps.destinationDeReference`), ou nulle part.
   */
  door?: string;
  /** Son jeu : la famille du SDK, au PLURIEL (comme `jeu.family`). */
  family: string;
  /** Et son sous-type, quand la porte en declare un. */
  type?: string;
  /** Le titre d'une fiche de cette porte. Repli : son id, jamais rien. */
  titleOf(record: Record<string, unknown>): string;
  /** Son sous-titre, quand la porte liee en declare un pour sa liste. */
  subtitleOf?(record: Record<string, unknown>): string | undefined;
  /**
   * CETTE FICHE PASSE-T-ELLE LA CONDITION DES CHOIX du champ (`choix` de la
   * reference declaree) ? Comme `titleOf`, elle prend une fiche BRUTE (un
   * document du port), et le contexte de la condition (moi, fuseau) est deja
   * lie quand l'assemblage la pose.
   *
   * ELLE FILTRE CE QUE LES SELECTEURS PROPOSENT, JAMAIS CE QU'UNE CELLULE
   * AFFICHE : un id deja pose hors condition se nomme, s'ouvre, et le
   * selecteur le garde en tete en disant « Hors des choix »
   * (`FICHE_HORS_DES_CHOIX`). ABSENTE : toute fiche passe.
   */
  choixPermis?(record: Record<string, unknown>): boolean;
  /**
   * LA FICHE VISEE EST UN MODELE, et `door` est la porte de sa famille cible :
   * la fleche l'y ouvre EN MODE MODELES (`mod=1`), le seul ou un modele se lit
   * (chantier `plan-sur-modele`, le modele d'un plan recurrent). Absent : une
   * fiche ordinaire, ouverte dans le jeu ordinaire de sa porte.
   */
  modeles?: boolean;
  /**
   * LE MOT MONTRE A LA PLACE D'UN ID QUE L'ANNUAIRE NE NOMME PAS, pour une
   * reference dont la valeur ne doit JAMAIS s'afficher : une personne
   * (`personId`, uid de `createdBy`). Present, la cellule ne montre ni l'id nu
   * ni « Supprimee ou introuvable » : rien tant que l'annuaire n'est pas la, ce
   * mot, attenue, quand il ne connait pas l'id. Absent : les quatre etats
   * ordinaires d'une reference (`referenceDeCellule`, `views/project.ts`).
   */
  repli?: string;
}

/**
 * CE QU'UN SAUT VERS UNE AUTRE PORTE PRECISE EN PLUS de la porte et de l'id.
 *
 * `modeles` FORCE LE MODE D'ARRIVEE : `true` ouvre la fiche en mode Modeles (la
 * fleche du modele d'un plan), `false` HORS de ce mode (le plan neuf cree
 * depuis un modele ouvert). Absent, le mode SUIT le saut : une carte liee d'un
 * modele ouvre ses enfants en mode Modeles, une fiche ordinaire ses fiches
 * ordinaires.
 */
export interface OptionsDeSaut {
  modeles?: boolean;
  /**
   * LE MOT DE LA PUCE DE SORTIE A L'ARRIVEE, quand on ne saute pas depuis une
   * fiche (chantier `fiche-suivi`) : une porte qui ouvre ses fiches dans une
   * autre (`auClic: { porte }`) part d'une COLLECTION (« Kanban : Projets »),
   * une porte Canvas de son nom. Absent : le titre de la fiche quittee, comme
   * pour une carte liee.
   */
  origine?: string;
}

/** Une colonne de tableau : la valeur sert au tri, a l'export et a l'agregat. */
export interface Column<T> {
  /** Cle stable de la colonne (cle de `ProjectedRow.cells`, cle de tri). */
  key: string;
  /**
   * LE TYPE DU CHAMP DECLARE, quand la colonne en vient (`colonnesDe`) : le
   * `type` de `taki.fields.json`, tel quel (un prix est un `number`, des liens
   * multiples un `lien`). Il dit ce que la valeur EST, la ou `kind` dit comment
   * on la saisit, et il reste pose sur une colonne en LECTURE (un calcul, un
   * numero, une colonne sans `set`) : c'est lui, avec les `reglages` qui
   * portent le format, que lisent les conditions de filtre, le pied d'un
   * montant (somme au centime) et la projection d'une liste de liens.
   */
  valueType?: TypeDeChamp;
  /** Les reglages d'affichage du champ (`ReglagesDeColonne`), quand il en declare. */
  reglages?: ReglagesDeColonne;
  /** En-tete affiche (francais). */
  label: string;
  /** La valeur comparable : tri, export, agregat. */
  value(record: T): Cell;
  /** Affichage seul (jamais trie, jamais exporte). */
  render?(record: T): ReactNode;
  /**
   * LE MEME AFFICHAGE, A PARTIR D'UNE VALEUR SEULE, quand `render` ne fait que
   * l'habiller (la pastille de couleur d'un choix, un montant, un taux). C'est
   * ce que l'ecriture OPTIMISTE montre dans la case, avant que le serveur ait
   * repondu : `render` lit la FICHE, qui porte encore l'ancienne valeur. Sans
   * lui, choisir « ml » montrait « ml » en texte brut, puis la pastille une a
   * deux secondes plus tard (capture 69 du fondateur, chantier `ecran-fluide`).
   * Absent : la case optimiste montre le libelle du catalogue, comme avant.
   */
  rendreValeur?(valeur: Cell): ReactNode;
  /** Colonne triable au clic sur l'en-tete. */
  sortable?: boolean;
  /**
   * CETTE COLONNE NE SE TRIE PAS AU SERVEUR (`false`) : sa valeur n'est stockee
   * nulle part, donc aucun `orderBy` ne la connait. C'est le cas d'une
   * `lecture` (le champ d'une fiche liee). En pagination CLIENTE elle se trie
   * comme une autre ; en pagination SERVEUR elle n'est pas triable.
   */
  triServeur?: false;
  /**
   * LES ANNUAIRES QUE CETTE COLONNE LIT en plus de sa `reference`, quand c'est
   * une `lecture` : un par etage de lien suivi (la fiche liee, puis celle que
   * son propre lien designe). `referencesDeBindings` les charge avec les
   * autres, c'est ce qui fait que la valeur lue est la quand la ligne s'affiche.
   */
  annuaires?: readonly ColumnReference[];
  /**
   * LE LIBELLE DU LIEN QUE CETTE LECTURE SUIT (le premier etage), pour dire sur
   * la fiche d'ou vient la valeur : « Lu sur la fiche liée « Animal » ».
   * Absent pour tout ce qui n'est pas une `lecture`.
   */
  lueSur?: string;
  /**
   * UN TOTAL LIE PAR LIGNE (`totauxLies`, `views/screens.ts`) : le kanban
   * l'ecrit sur sa carte, a droite du sous-titre (`ProjectedRow.totauxLies`).
   */
  totalLie?: true;
  /** Agregat de pied de colonne. */
  agg?: 'sum' | 'count' | 'avg';
  /**
   * L'HABILLAGE DE L'AGREGAT, quand la colonne en a un.
   *
   * `render` habille une CASE : il recoit la fiche, et un total n'en a aucune.
   * Sans ce second point, une colonne en euros affichait « 18 500 € » sur
   * chaque ligne et « 21 550 » tout court en pied, comme si le total parlait
   * d'autre chose. `count` n'est jamais habille : c'est un nombre de fiches,
   * pas une valeur de la colonne.
   */
  formatAgg?: (valeur: number) => string;
  /**
   * LE CHEMIN DE STOCKAGE de la colonne, quand il n'est pas sa clef.
   *
   * Un champ SUR-MESURE ne vit pas a la racine du document : il vit dans le sac
   * `customFields` (AGENTS.md section 7). `cout` s'AFFICHE, `customFields.cout`
   * se somme et se trie AU SERVEUR, et viser la clef nue la-bas ne leve rien :
   * `sum('cout')` rend 0 et `orderBy('cout')` exclut tous les documents.
   *
   * ABSENT = la colonne est stockee sous sa clef (un champ natif de la
   * famille). L'assemblage le pose (`views/screens.ts`, `cheminDeStockage`) ;
   * une app qui ecrit ses colonnes a la main le pose si elle interroge le
   * serveur sur elles.
   */
  path?: string;
  /**
   * Ecriture de la colonne. Sa PRESENCE est ce qui rend la case editable en
   * fiche (`ProjectedRow.cells[key].editable`) : sans elle, aucune affordance,
   * pas meme au survol. Signature IDENTIQUE a `FieldMap.set` au caractere pres,
   * pour que les deux entrees d'ecriture partagent le meme chemin dans le core.
   *
   * Ce que l'editeur emet, selon `kind` : une chaine pour `text`, la `value`
   * d'une `Option` pour `select`, un nombre ou `null` pour `number`, une chaine
   * `YYYY-MM-DD` (date PURE, jamais un instant) ou `null` pour `date`, les
   * clefs cochees une par ligne (ou `null`) pour `multiselect`. A l'app de
   * traduire vers son champ metier.
   */
  set?(record: T, value: Cell): Promise<void>;
  /**
   * Type d'editeur. Par defaut, deduit de la valeur (nombre -> `number`,
   * Date -> `date`, sinon `text`). `'select'` EXIGE `options`.
   */
  kind?: EditorKind;
  /**
   * Catalogue de la colonne, pour `kind: 'select'` et `'multiselect'`. Resolu au RUNTIME (reglages
   * du tenant), comme celui d'un role. La fiche le lit par `optionsOf(key)` :
   * les ROLES d'abord, les colonnes ensuite.
   *
   * PIEGE : la `value()` de la colonne doit rendre la valeur STOCKEE (la `value`
   * d'une `Option`), pas son libelle, sinon l'editeur ne selectionne jamais
   * rien. Une colonne qui tient a trier sur le mot affiche garde son `render()`
   * pour l'habillage.
   */
  options?(): Option[];
  /**
   * LE PLACEHOLDER DE LA CASE VIDE (« SNY-FX9-003 »), declare champ par champ
   * dans le Studio et recopie ici par `colonnesDe`.
   *
   * OU IL SE REND : dans la fiche de CREATION (une case vide invite a ecrire au
   * lieu d'annoncer « Non renseigne ») et dans l'editeur, sur `input` et
   * `textarea`. Sur une fiche existante, une case vide continue de dire
   * « Non renseigne » : rien n'y a ete saisi, ce n'est pas la meme phrase.
   */
  placeholder?: string;
  /**
   * LE CHAMP EST OBLIGATOIRE (`required` de `taki.fields.json`), recopie tel
   * quel par `colonnesDe`.
   *
   * IL NE BLOQUE PAS UNE ECRITURE ORDINAIRE : une fiche existante peut avoir
   * une case obligatoire vide (elle a ete creee avant la regle), et refuser de
   * l'enregistrer bloquerait le travail sur le reste de la fiche. Il ne compte
   * qu'a la CREATION, ou le moteur reclame les cases vides avant d'appeler le
   * port.
   */
  required?: boolean;
  /**
   * LE CATALOGUE DE CETTE FICHE, quand il depend d'une autre de ses valeurs (un
   * Job ne propose que les jobs du Pole de la fiche, `dependDe`). Pose par
   * l'assemblage, jamais par une app ; la projection le range dans la cellule
   * (`ProjectedCell.options`) et la fiche le prefere a `options()`. Absent :
   * le catalogue ne depend de rien.
   */
  optionsPour?(record: T): Option[];
  /**
   * CE QUE CETTE ECRITURE VIDE AUSSI, en patch du sac : changer de Pole vide
   * le Job devenu incompatible (`{ job: null }`). Lu par le brouillon de
   * creation, qui n'ecrit rien au serveur ; une fiche reelle le fait dans
   * l'acces au jeu, dans le meme patch. Absent : rien d'autre ne bouge.
   */
  videAussi?(record: T, value: Cell): Record<string, readonly string[] | null>;
  /**
   * LA VALEUR QUE CE CALCUL PREND SUR CETTE FICHE, par l'evaluateur du
   * data-model (`recalculer`), celui-la meme qui tourne a la creation et dans
   * le declencheur serveur. Pose par l'assemblage sur une colonne `calcul`,
   * jamais par une app. Lu par le brouillon de creation seulement : la fiche
   * qui n'existe pas encore montre son total avant « Creer », sans rien ecrire.
   * Une fiche reelle le recoit de l'acces au jeu, dans le patch de la saisie.
   */
  calcule?(record: T): Cell;
  /**
   * CETTE COLONNE PORTE L'ID D'UNE FICHE D'UNE AUTRE PORTE (`ColumnReference`).
   *
   * POSEE PAR L'ASSEMBLAGE, JAMAIS PAR UNE APP : elle se deduit des liens que
   * les AUTRES portes de l'ecran declarent vers celle-ci. Ce qu'elle change :
   * la cellule projetee gagne le LIBELLE de la fiche designee (`value` reste
   * l'id, c'est ce qui s'ecrit), et la colonne prend l'editeur `lien` quand
   * elle porte un `set`.
   *
   * ABSENTE = la colonne porte une valeur, pas une relation : rien ne change.
   */
  reference?: ColumnReference;
}

/**
 * CE QUE L'ECRAN DECLARE, par type de vue. Un binding par vue proposee :
 * la meme donnee ne se raconte pas pareil en liste et en kanban.
 */
export interface ViewBinding<T> {
  /** Le type de vue auquel ce binding s'applique. */
  type: ViewId;
  /**
   * Les champs metier projetes sur des roles. Les clefs connues sont celles de
   * `FieldRole` ; une clef libre alimente `ProjectedRow.extra` (roles propres
   * a une vue custom, ex. 'lat' / 'lng').
   */
  roles: Partial<Record<FieldRole, FieldMap<T>>> & Record<string, FieldMap<T>>;
  /** Les colonnes (tableau, et toute vue qui compare des valeurs). */
  columns?: Column<T>[];
  /** Champs de la porte, indépendants des colonnes affichées. */
  fieldColumns?: Column<T>[];
  /** Sac libre, lu par CE SEUL type de vue (`ViewProps.options`). */
  options?: Record<string, unknown>;
  /**
   * LA SURBRILLANCE DES LIGNES, lue dans l'ordre (`Highlight`). ABSENTE = la
   * regle par defaut, « Nouveau » et elle seule (`SURBRILLANCE_PAR_DEFAUT`) :
   * un kanban code a la main garde la marque des fiches neuves sans rien
   * declarer. La projection la pose sur `ProjectedRow.highlight`.
   */
  highlights?: Highlight<T>[];
}

/**
 * UNE REGLE DE SURBRILLANCE (`docs/socle/cartes-kanban.md`, section 2).
 *
 * `when` est le mot `'nouveau'` (la fiche jamais ouverte sur ce poste, c'est-a-
 * dire `ProjectedRow.isNew`) ou un predicat sur l'enregistrement. « Nouveau »
 * n'est donc PAS un cas a part dans la carte : c'est une regle comme une autre,
 * et la vue ne lit que le resultat (`RowHighlight`).
 */
export interface Highlight<T> {
  when: 'nouveau' | TestDeSurbrillance<T>;
  /** La couleur de la bordure et de l'ombre, et celle de l'etiquette. */
  color: Couleur;
  /** L'etiquette posee en tete du bas de carte. Absente : la couleur seule. */
  label?: string;
}

/**
 * LA SURBRILLANCE D'UNE LIGNE, deja tranchee par la projection : la PREMIERE
 * regle vraie donne `color` ; CHAQUE regle vraie qui porte un `label` pose son
 * etiquette, dans l'ordre des regles. Une regle vraie sans label colore (si
 * elle est la premiere) sans rien ecrire.
 */
/**
 * Le test d'une regle, ecrit en METHODE : une methode est bivariante (comme
 * `FieldMap.get`), une propriete fonction ne l'est pas, et elle rendrait
 * `ViewBinding<T>` invariant : le cockpit range alors des bindings de lignes
 * differentes dans un meme tableau, et plus rien ne compile.
 */
type TestDeSurbrillance<T> = { test(record: T): boolean }['test'];

export interface RowHighlight {
  color: Couleur;
  labels: { label: string; color: Couleur }[];
}

/* ------------------------------------------------------------------ */
/* Les LIENS entre portes : ce qu'une fiche montre d'un AUTRE jeu       */
/* ------------------------------------------------------------------ */

/**
 * UN LIEN declare par une porte vers une AUTRE porte du meme ecran : « les
 * interventions de CE materiel », « les heures de CE projet ».
 *
 * IL NE PORTE QUE DES NOMS. La porte liee, son jeu, et le champ de ce jeu qui
 * porte l'id de la fiche ouverte. Ce n'est pas une requete : c'est de quoi en
 * ecrire une, et c'est l'hote qui la fait, avec le SDK, cote serveur.
 *
 * `family` ET `type` VOYAGENT AVEC : sans eux, l'hote ne saurait pas dans quel
 * repository demander les fiches liees, et devrait retrouver la porte pour le
 * deduire. Le lien assemble est ce qu'on tient en main, pas un renvoi de plus.
 */
interface LienDeBase {
  /** L'id du lien, tel que les cartes et les totaux le citent. */
  id: string;
  /** L'id de la porte liee. */
  door: string;
  /** Le jeu lie : sa famille du SDK. */
  family: string;
  /** Et son sous-type, quand la porte en declare un. */
  type?: string;
}

/**
 * LE LIEN PAR LE CHAMP DU JEU LIE (`par`) : une egalite, ou une APPARTENANCE
 * quand `multiple` est vrai.
 *
 * `by` est le CHEMIN DE STOCKAGE, dans le jeu lie, du champ qui porte l'id de
 * la fiche parente (`assetId`, `customFields.materielId`). `multiple` est pose
 * par l'assemblage, jamais declare : le champ du jeu lie est un `lien` a
 * `plusieurs: true`, sa valeur est une LISTE d'ids, et les fiches liees sont
 * celles dont la liste CONTIENT l'id de la fiche ouverte (sens inverse).
 */
export interface LienParLeChampLie extends LienDeBase {
  by: string;
  multiple?: true;
}

/**
 * LE LIEN PAR LA LISTE (`liste`), le SENS DIRECT : le champ liste vit sur la
 * fiche OUVERTE, et les fiches liees sont celles que cette liste CITE.
 *
 * `liste` est le CHEMIN DE STOCKAGE de ce champ dans le jeu de la porte qui
 * DECLARE le lien (`customFields.produitsDemandes`). Il n'y a AUCUN cadrage
 * serveur : les ids se lisent sur la fiche ouverte, puis les fiches par leurs ids.
 */
export interface LienParLaListe extends LienDeBase {
  liste: string;
}

export type LinkedDoor = LienParLeChampLie | LienParLaListe;

/** Le lien est-il le sens direct (la liste de la fiche ouverte) ? */
export function estLienParListe(lien: LinkedDoor): lien is LienParLaListe {
  return 'liste' in lien && typeof lien.liste === 'string';
}

/**
 * UN AGREGAT SERVEUR sur le jeu lie.
 *
 * IL SE DEMANDE, IL NE SE CALCULE PAS SUR CE QU'ON A EN MEMOIRE : une carte
 * liee ne charge qu'un PLAFOND de fiches, donc compter ce qu'elle affiche
 * mentirait des la 201e (AGENTS.md section 7). `field` est absent pour `count`,
 * qui compte les fiches et ne porte aucun champ.
 */
export interface LinkedAggregate {
  agg: 'count' | 'sum' | 'avg';
  /** Le CHEMIN DE STOCKAGE du champ agrege (`amount`, `customFields.cout`), jamais sa clef nue. */
  field?: string;
}

/**
 * UNE CARTE DE FICHE QUI MONTRE LES FICHES LIEES, par une vue du socle.
 *
 * ELLE NE DESSINE RIEN ELLE-MEME, et c'est ce qui la rend tenable : elle porte
 * le binding de la porte LIEE pour la vue demandee, assemble par les memes
 * fonctions que l'ecran. La liste et le tableau du socle rendent donc les
 * fiches liees exactement comme ils rendent les autres.
 */
export interface LinkedCardDef<T = unknown> {
  /** Le titre de la carte. */
  title: string;
  /**
   * L'ID DE LA PORTE QUI HEBERGE LA CARTE, c'est-a-dire celle dont la FICHE est
   * ouverte. Pose par l'assemblage, qui la connait (`carteLieeDe`).
   *
   * A QUOI IL SERT, ET IL N'Y A QUE CA : RETIRER LA COLONNE REDONDANTE. Le
   * binding de la porte liee porte ses colonnes de reference, dont celle qui
   * pointe vers ICI : sur la fiche d'un materiel, la carte « Interventions »
   * repeterait le nom de ce materiel a chaque ligne. Une colonne dont chaque
   * case dit le titre au-dessus d'elle n'apprend rien et mange la largeur.
   */
  porte: string;
  /**
   * LE JEU DE LA FICHE OUVERTE (famille du SDK et sous-type), quand la porte qui
   * heberge la carte en declare un. Il ne sert qu'au SENS DIRECT (`liste`) : la
   * fiche creee depuis la carte s'ajoute a la liste de la fiche ouverte, et le
   * port des liens veut la famille de celle qu'il ecrit.
   */
  parent?: { family: string; type?: string };
  /** Le lien qu'elle suit, deja resolu. */
  link: LinkedDoor;
  /**
   * LES COLONNES DE LA CARTE SONT DECLAREES (`colonnes`, `taki.screens.json`) :
   * le binding porte EXACTEMENT celles-la, et les regles qui retirent une colonne
   * redondante (`sansColonneVersLaPorte`, `sansColonnesRedites`) ne s'appliquent
   * plus. Elles ne valent que pour des colonnes heritees de la porte liee.
   */
  colonnesDeclarees?: true;
  /** La vue montee dans la carte : deux, et pas une troisieme. */
  view: 'core.liste' | 'core.tableau';
  /** Combien de fiches liees la carte charge, au plus. */
  limit: number;
  /** Le binding de la porte liee pour `view` : ses roles, ses colonnes, son sac. */
  binding: ViewBinding<T>;
  /** Le tri par defaut de l'ecran, resolu pour la porte liee. */
  sort: SortLevel[];
  /**
   * LES TOTAUX DE PIED que cette vue demande au SERVEUR : une entree par colonne
   * qui declare une SOMME ou une MOYENNE. Vide pour `core.liste`, qui n'a pas de
   * pied.
   *
   * `compte` N'Y EST PAS : sous une colonne, un compte annonce les CASES
   * REMPLIES, alors que le serveur compte des DOCUMENTS. Deux nombres pour un
   * meme mot, et le pied garde celui qui parle de sa colonne.
   */
  totals: Array<{ key: string } & LinkedAggregate>;
  /**
   * L'ACTION « add » DE LA PORTE LIEE, quand elle en declare une : c'est elle
   * que « + Ajouter » demandera a l'arrivee, dans la porte liee.
   *
   * ABSENTE = LA CARTE N'A PAS DE BOUTON, et surtout pas un bouton grise. Meme
   * regle que la rangee d'actions : une porte qui ne declare rien n'ajoute
   * rien, et un bouton grise ferait croire a un droit manquant, ce qui envoie
   * chercher la panne dans les rules.
   */
  ajout?: { actionId: string };
  /**
   * LE BINDING DE LA PORTE LIEE POUR `core.tableau`, quand elle en declare un :
   * celui que la FENETRE DE CHOIX monte (« Lier une fiche »,
   * `views/core/LinkedPicker.tsx`).
   *
   * POURQUOI UN SECOND BINDING PLUTOT QUE `binding`. La carte monte la vue
   * qu'elle DECLARE, et c'est souvent `core.liste` : un binding de liste ne
   * porte aucune colonne, donc la fenetre de choix n'aurait affiche qu'un nom
   * et un statut, la ou le fondateur en attend le tableau de la porte (« on
   * reprend vraiment les elements qu'on a deja »). Choisir une fiche, c'est
   * COMPARER des valeurs : c'est le travail du tableau, pas celui de la liste.
   *
   * ABSENT = la fenetre retombe sur `binding`. Une porte qui ne declare aucun
   * detail `core.tableau` n'a pas de colonnes a montrer, et c'est un fait de
   * declaration, pas une panne.
   */
  bindingChoix?: ViewBinding<T>;
  /**
   * LES FILTRES DECLARES PAR LA PORTE LIEE (`filtres`, `taki.screens.json`),
   * pour la FENETRE DE CHOIX et pour elle seule.
   *
   * ILS TRAVERSENT PARCE QUE LA CARTE N'A AUCUN AUTRE MOYEN DE LES ATTEINDRE :
   * elle ne connait que son lien et un binding, pas la declaration d'ecran. Et
   * sans eux, la fenetre proposerait une fenetre de 500 fiches a trancher a la
   * seule recherche.
   *
   * ILS NE FILTRENT RIEN DANS LA CARTE : ses lignes sont cadrees par le lien,
   * il n'y a pas d'autre tranche a y poser.
   */
  filtres?: readonly LinkedFilterDef<T>[];
}

/**
 * UN FILTRE DU CATALOGUE D'UNE PORTE LIEE, tel que la fenetre de choix le
 * recoit. MEME FORME QUE `FiltreAssemble` (`views/screens.ts`), et c'est
 * exactement le meme objet : l'assemblage le fabrique une fois, par la fonction
 * qui sert deja le moteur d'ecran.
 *
 * `match` EST FACULTATIF comme la-bas : un filtre sans predicat ne retient rien
 * et ne se compte pas, plutot que de retenir tout en silence.
 */
export interface LinkedFilterDef<T = unknown> {
  id: string;
  label: string;
  match?(record: T): boolean;
}

/**
 * CE QUE LES DEUX GESTES D'UN CHAMP DE LIEN ONT EN MAIN (« Lier une fiche
 * existante », « Ajouter une fiche liée »), assemble pour la fiche et pour
 * elle seule (chantier `champ-lie-deux-gestes`).
 *
 * LA MEME FENETRE ET LA MEME CREATION QUE LA CARTE LIEE (`LinkedCardDef`), et
 * c'est pour ca que les champs portent les memes noms : `bindingChoix`,
 * `filtres` et `ajout` sortent de la meme fonction d'assemblage
 * (`fenetreDeChoixDe`, `views/screens.ts`). Seul le SENS change : la carte
 * ecrit l'ENFANT (le lien inverse), le champ ecrit la fiche OUVERTE.
 *
 * IL VOYAGE DANS LE SAC DU BINDING DE FICHE (`options.choixDeReference`, une
 * entree par clef de colonne), comme les cartes : `ViewColumn.reference` est
 * de la DONNEE, et un binding porte des fonctions qui ne traversent pas la
 * frontiere de la vue par la colonne.
 */
export interface ChoixDeReferenceDef<T = unknown> {
  /** Le jeu VISE par le champ : ses fiches sont les candidates. */
  family: string;
  type?: string;
  /** La porte du MEME ecran qui montre ce jeu, quand il y en a une. */
  door?: string;
  /**
   * CE QUE LA FENETRE MONTE : le tableau de la porte visee quand elle en
   * declare un, sinon sa liste. SANS PORTE dans l'ecran, un binding fait du
   * seul titre (et du sous-titre) de la reference : on nomme les fiches, on ne
   * pretend pas connaitre les colonnes d'une autre app.
   */
  bindingChoix: ViewBinding<T>;
  /** Les filtres declares par la porte visee, pour la fenetre seulement. */
  filtres?: readonly LinkedFilterDef<T>[];
  /**
   * L'AJOUT DE LA PORTE VISEE, et SEULEMENT S'IL EST TENU PAR LE SOCLE (la
   * fiche vide, action `add` sans `fn`) : c'est la fiche vide qui sait rendre
   * l'id qu'elle cree au champ d'origine. Une porte dont l'app cree par sa
   * propre fenetre ne le saurait pas, et « Ajouter » n'y est pas propose.
   */
  ajout?: { actionId: string };
  /**
   * CE QUE « Ajouter » RECRIRA une fois la fiche creee : le jeu de la porte QUI
   * PORTE LA FICHE OUVERTE, et le chemin de stockage du champ (`contactId`,
   * `customFields.clientId`). L'id de la fiche ouverte se lit au clic.
   */
  retour: { family: string; type?: string; chemin: string };
  /**
   * LA CONDITION DES CHOIX du champ (`ColumnReference.choixPermis`), pour la
   * fenetre « Lier une fiche » : une fiche qui ne la passe pas n'est pas
   * proposee. Elle prend un document BRUT du port, comme `titleOf`.
   */
  choixPermis?(record: Record<string, unknown>): boolean;
}

/**
 * UN CHAMP DE TOTAL sur la fiche parente : une case de carte qui ne lit aucun
 * champ de la fiche, mais COMPTE les fiches liees (« Interventions : 12 »).
 */
export interface RollupFieldDef {
  /** Le mot devant la valeur : ce total n'est le champ d'aucun jeu. */
  label: string;
  /** Le lien qu'il suit, deja resolu. */
  link: LinkedDoor;
  agg: LinkedAggregate['agg'];
  /** Le CHEMIN DE STOCKAGE, dans le jeu lie, du champ agrege (`customFields.cout`). Absent pour `count`. */
  field?: string;
  /**
   * L'HABILLAGE DU TOTAL, celui de la colonne agregee.
   *
   * Sans lui, la fiche d'un materiel affichait « 18 500 € » pour sa valeur
   * d'achat et « 1890 » pour le cout de ses interventions, deux sommes en euros
   * ecrites dans deux langues a six lignes d'ecart. `count` n'en recoit jamais :
   * c'est un nombre de fiches.
   */
  format?: (valeur: number) => string;
  /**
   * LA PONDERATION de la somme (`fois` du fichier d'ecrans) : chaque valeur de
   * `field` est multipliee par un nombre lu sur la fiche que la ligne DESIGNE.
   * Presente, le total ne se demande plus au serveur comme une somme : l'agregat
   * serveur ne sait pas lire une autre fiche, la carte charge donc les lignes,
   * resout les fiches designees EN LOT et additionne (`totalPondere`,
   * `lib/liens.tsx`). `agg` vaut alors toujours `sum`.
   *
   * Tous les chemins sont des CHEMINS DE STOCKAGE (`customFields.articleId`),
   * jamais des noms nus.
   */
  times?: {
    /** Le champ de reference du jeu LIE, qui porte l'id de la fiche designee. */
    by: string;
    /** Le jeu designe : sa famille du SDK, au pluriel (`products`)... */
    family: string;
    /** ... et son sous-type, quand la reference en declare un. */
    type?: string;
    /** Le champ numerique de la fiche designee (`customFields.priceInclTax`). */
    field: string;
  };
}

/**
 * UN ENFANT D'UN MODELE DE FICHE (chantier `modeles-enfants`) : une fiche liee
 * que le modele EMPORTE, telle que `sdk.templates` la range dans `children`.
 *
 * C'est le contrat qui relie trois choses qui ne se voient pas : le port des
 * modeles (`lib/templates.tsx`, qui relaie les `Template` de l'hote), le
 * moteur d'ecran (qui modifie un enfant comme une case de modele) et la carte
 * liee (qui le projette en mode Modeles). Le type vit ICI, avec `LinkedDoor`,
 * parce qu'un enfant se reconnait par le meme vocabulaire qu'un lien :
 * `family` et `type` sont le jeu de la porte liee, `linkKey` est le chemin de
 * stockage `by` du lien (`assetId`, ou `customFields.materielId`).
 *
 * `id` EST STABLE dans le tableau `children` du modele : pose par le SDK a
 * l'ecriture, ou par le socle a l'ajout. C'est lui qui rend une case d'enfant
 * adressable (`saveState.target`, pile d'annulation, selection), la ou un rang
 * de tableau changerait sous une suppression.
 */
export interface EnfantDeModele {
  id: string;
  /** Le repository du SDK, au PLURIEL (`careRecords`), comme `jeu.family`. */
  family: string;
  /** Le sous-type que portera la fiche creee (`intervention`). */
  type: string;
  /** Le CHEMIN DE STOCKAGE du champ qui recevra l'id de la racine a l'instanciation. */
  linkKey: string;
  /** Les champs metier a recopier, `customFields` compris, SANS le champ de lien. */
  fields: Record<string, unknown>;
}

/**
 * LA FICHE OUVERTE EST UN MODELE (mode Modeles), et voici ses enfants. Posee
 * par le moteur sur `ViewProps.modele` au niveau fiche seulement ; la carte liee
 * s'en sert a la place du port des liens : un modele n'a rien a demander au
 * serveur, ses fiches liees sont dans son document.
 *
 * ELLE NE PORTE QUE DE LA LECTURE, DEPUIS LE PIVOT DU 10/09/2026. La version
 * d'avant donnait trois verbes a la carte (`ecrire`, `ajouter`, `retirer`) :
 * elle rendait alors des SOUS-FICHES editables en place, une fiche dans la
 * fiche. Le fondateur a tranche l'inverse : la carte redevient une LISTE en
 * lecture, et un enfant s'ouvre, se modifie, s'ajoute et se retire COMME UNE
 * FICHE, dans SA porte (adresse `<modeleId>/<enfantId>`, `cheminDEnfant`). Les
 * trois verbes vivent donc la ou vivent deja ceux d'une fiche : le chemin
 * ordinaire du moteur (`onPatchColumn`), le brouillon (`ouvrirBrouillon`) et le
 * menu « ... » (`recordMenuActions`).
 *
 * `name` EST LA POUR LE GESTE D'AJOUT : « + Ajouter » passe dans la porte liee
 * et y ouvre une fiche vide dont la case du lien est VERROUILLEE sur le nom du
 * modele parent. Un id nu dans cette case ne dirait rien a personne.
 */
export interface ModeleOuvert {
  /** L'id du modele ouvert (celui qui compose l'adresse de chaque enfant). */
  id: string;
  /**
   * Son nom : ce que la case verrouillee du lien affiche, sur le brouillon d'un
   * enfant comme sur la fiche d'un enfant deja la (le moteur y pose le meme mot,
   * voir `ViewProps.verrouillees`).
   */
  name: string;
  enfants: readonly EnfantDeModele[];
}

/** Ce que l'annuaire sait d'UNE fiche : de quoi la nommer, et son etat. */
export interface FicheDAnnuaire {
  /** Le titre, tel que la porte referencee l'affiche dans ses propres listes. */
  title: string;
  /** Son sous-titre, quand la porte liee en declare un pour sa liste. */
  subtitle?: string;
  /** La photo, pour une personne qui en a posé une (https). */
  photo?: string;
  /**
   * SON SOUS-TYPE (`type` du document), quand il en porte un.
   *
   * POURQUOI L'ANNUAIRE LE GARDE. Une reference declaree SANS type (« un
   * client : `contacts`, personne ou societe ») sait nommer la fiche mais pas
   * dire quelle porte l'ouvre quand l'app qui montre la famille n'a que des
   * portes TYPEES (Contact : personnes, societes). Le type de la fiche visee
   * tranche : c'est lui que `destinationDeReference` transmet au shell quand la
   * reference n'en fixe pas.
   */
  type?: string;
  /**
   * LA FICHE EST ARCHIVEE (`archivedAt` pose).
   *
   * L'ANNUAIRE CHARGE LES ARCHIVES, ET IL LE FAUT : une intervention qui pointe
   * un materiel archive doit afficher son NOM, pas son id attenue avec
   * « Supprimee ou introuvable ». Une archive n'est pas une fiche effacee.
   * Ce qui change, c'est ce que la surface en fait : elle s'AFFICHE (attenuee,
   * avec son mot au survol) et elle ne se PROPOSE plus dans un choix, sauf
   * quand elle est deja la valeur en place.
   *
   * UNE FICHE SUPPRIMEE, ELLE, N'ENTRE JAMAIS DANS UN ANNUAIRE, et ce champ ne
   * vaut donc jamais pour elle : `archived: 'all'` la ramene du serveur (elle
   * porte `archivedAt`), mais le SDK la retire cote client avant que l'annuaire
   * ne la voie (`runList`, `packages/sdk/src/list-query.ts`). C'est ce qui rend
   * la regle de la cellule lisible : ce qui est ICI se NOMME, ce qui n'y est pas
   * se dit « Supprimee ou introuvable ». Il n'y a pas de troisieme etat a
   * afficher, et le socle n'a rien pour le distinguer d'un id orphelin.
   */
  archive: boolean;
  /**
   * LA FICHE BRUTE, telle que le port l'a rendue : champs standard a la racine,
   * sac `customFields` compris. Ce que lit un champ `lecture` (le champ d'une
   * fiche liee, `docs/socle/champ-lie-lecture.md`), qui la lit a l'affichage
   * plutot que de la recopier. Absent pour l'annuaire des personnes, qu'aucune
   * lecture ne vise.
   */
  valeurs?: Readonly<Record<string, unknown>>;
  /**
   * LE DOCUMENT BRUT de la fiche, tel que le port l'a rendu, pour que
   * `ColumnReference.choixPermis` puisse lire les champs que la condition
   * cite. Pose par la lecture d'un jeu de fiches (jamais par l'annuaire des
   * personnes, qui n'a pas de condition de choix) ; absent, la fiche passe.
   */
  document?: Record<string, unknown>;
}

/**
 * L'ANNUAIRE D'UN JEU : le titre et le sous-titre de ses fiches, par id, ET LE
 * FAIT DE SAVOIR SI LA FENETRE COUVRE LE JEU.
 *
 * A QUOI IL SERT, ET A QUOI IL NE SERT PAS. Une colonne de REFERENCE stocke un
 * id (« un lien stocke un id, jamais un libelle recopie ») : sans annuaire, une
 * ligne affiche `m1RxQ2...` la ou l'utilisateur attend « Sony FX9 ». L'annuaire
 * est la table de correspondance, chargee UNE fois pour le jeu entier par
 * l'hote, pas une lecture par ligne : c'est ce qui ferme le fork ou chaque
 * ecran chargeait une collection pour resoudre des noms.
 *
 * IL NE PORTE QUE DES FAITS DEJA LUS : le titre resolu de chaque fiche, et sa
 * fiche brute (`FicheDAnnuaire.valeurs`, pour les champs `lecture`). Aucune
 * promesse : la projection est SYNCHRONE et ne va rien chercher.
 *
 * `complet` VOYAGE AVEC LES FICHES, DANS LE MEME OBJET, et ce n'est pas un
 * confort : c'est ce qui permet a la PROJECTION et a la FICHE de dire la MEME
 * chose d'un id inconnu. Les deux l'ont dit differemment une fois (la
 * projection affirmait « Supprimee ou introuvable » sans regarder la fenetre, la
 * fiche relisait l'etat du magasin pour la corriger) : un fait lu a deux
 * endroits est un fait qui divergera. Les TROIS etats sont donc uniques :
 * connu, c'est le titre ; inconnu et `complet`, c'est « introuvable » ; inconnu
 * et incomplet, c'est l'id NU, sans affirmation.
 */
export interface Annuaire {
  /** Les fiches resolues, par id. */
  fiches: ReadonlyMap<string, FicheDAnnuaire>;
  /**
   * LA FENETRE CHARGEE COUVRE-T-ELLE LE JEU ? Faux, un id absent n'est pas une
   * fiche introuvable : c'est peut-etre une fiche hors fenetre, et aucune
   * surface ne doit affirmer le contraire.
   */
  complet: boolean;
  /**
   * LES IDS DONT LE NOM EST EN COURS DE LECTURE, hors de la fenetre. Un id
   * demande par une surface et absent de la fenetre n'est pas « inconnu » : sa
   * fiche se lit A LA DEMANDE, par lot (`souhaiterTitres`,
   * `lib/annuaires.tsx`). Le temps de la lecture la cellule ne montre RIEN,
   * jamais l'id nu. Une fois la lecture revenue, l'id est soit dans `fiches`,
   * soit hors de cet ensemble (fiche supprimee : l'id nu d'avant).
   */
  enAttente?: ReadonlySet<string>;
}

/**
 * LES ANNUAIRES CHARGES, par clef de jeu (`cleDeJeu(family, type)`).
 *
 * LA CLEF EST UNE FONCTION PURE, ET UNE SEULE : celui qui remplit la carte et
 * celui qui la lit doivent fabriquer la meme chaine, sinon la lecture rend
 * toujours `undefined` et la colonne affiche des ids sans que rien ne le dise.
 *
 * UNE CLEF ABSENTE ET UN ANNUAIRE VIDE NE DISENT PAS LA MEME CHOSE : absente,
 * l'annuaire n'est pas encore charge (la cellule montre l'id nu) ; presente et
 * vide, il l'est et ne connait pas cet id (la cellule le DIT).
 */
export type Annuaires = ReadonlyMap<string, Annuaire>;

/* ------------------------------------------------------------------ */
/* Ce que le CORE produit                                              */
/* ------------------------------------------------------------------ */

/**
 * UNE CELLULE PROJETEE : la valeur comparable, son rendu, son editabilite, et
 * le LIBELLE quand la valeur designe une fiche d'ailleurs.
 *
 * `value` RESTE LA VALEUR STOCKEE, toujours : c'est elle qui s'ecrit, c'est
 * elle que l'editeur relit, et une cellule de reference porte donc l'ID.
 * `label` est ce que l'ANNUAIRE en a fait (« Sony FX9 ») : tout ce qui
 * s'affiche, se cherche et se trie lit `label ?? value`, dans cet ordre et
 * nulle part autrement, sinon l'ecran chercherait sur un mot et trierait sur un
 * id.
 *
 * `label` ABSENT est le cas normal : la colonne n'est pas une reference, ou
 * l'annuaire n'est pas charge, ou il ne connait pas cet id.
 */
export interface ProjectedCell {
  value: Cell;
  node: ReactNode;
  editable: boolean;
  /**
   * LE MOT QUI EST A L'ECRAN, quand il n'est pas la valeur stockee. C'est lui
   * qui sert a CHERCHER et a TRIER (`cellToText`, `sortValueOf`), jamais a
   * ecrire.
   *
   * Deux colonnes en portent un : une REFERENCE (la valeur est un id, le
   * libelle est le titre de la fiche designee) et une colonne a CATALOGUE (la
   * valeur est la clef `retail`, le libelle est « Commerce »). Sans lui, une
   * liste rangeait ses secteurs francais dans l'ordre de leurs clefs anglaises,
   * et chercher « Commerce » ne trouvait rien de ce qui est affiche.
   */
  label?: string;
  /** Le catalogue propre a cette fiche (`Column.optionsPour`), quand il en a un. */
  options?: Option[];
}

/**
 * CE QUE LA VUE RECOIT : une ligne neutre, plus aucun generique.
 *
 * C'est la frontiere qui evite le `any` : les vues du core manipulent
 * `ProjectedRow`, jamais `T`. Corollaire assume : une vue n'affiche que ce qui
 * est passe par un role ou par une colonne.
 */
export interface ProjectedRow {
  /** Id de l'enregistrement (celui rendu par `getId`). */
  id: string;
  /** Role `title`, deja mis en texte. Jamais vide : repli sur l'id. */
  title: string;
  /**
   * LA VALEUR STOCKEE DU TITRE, quand son champ a un CATALOGUE (un Service, une
   * Nature) : `''` sans valeur. Absente pour un titre en texte libre. Le titre
   * reste un TEXTE en lecture seule (le libelle du choix) : la fiche ne l'ouvre
   * jamais en liste de choix (decision du 02/11/2026), le choix se change dans
   * son champ.
   */
  titreAChoix?: string;
  /**
   * LE TITRE REPREND UNE FICHE LIEE (une consultation se titre par son animal,
   * une ligne de protocole par son article) : de quoi OUVRIR cette fiche, et
   * rien qui permette de l'editer. Absent pour un titre qui est du texte, un
   * catalogue ou un titre fait de plusieurs champs. Le `title` de la ligne est
   * le NOM de la fiche designee (ou son id tant que l'annuaire charge).
   *
   * UN TITRE EST UN TEXTE, JAMAIS UN MENU DE CHOIX : le clic sur ce titre ouvre
   * la fiche liee (le saut de la fleche d'un champ `lien`), le lien se change
   * dans son champ, plus bas dans la fiche.
   *
   * DE LA DONNEE, JAMAIS UNE FONCTION (comme `ViewColumn.reference`).
   */
  titreLie?: {
    reference: { door?: string; family: string; type?: string; modeles?: boolean };
    /** L'id designe, `''` tant que rien n'est choisi. */
    valeur: string;
    /** Le mot du champ (« Article »), quand le catalogue de l'ecran le nomme. */
    libelle?: string;
  };
  /**
   * CE QUE LE TITRE D'UN BROUILLON DIT, en gris, tant que la fiche liee ou le
   * choix qu'il reprend n'a pas de valeur : le `vide` de la source, sinon le mot
   * du champ (« Article »). Absent pour un titre texte, et quand le titre a une
   * valeur.
   */
  titreSansValeur?: string;
  /**
   * LES TOTAUX LIES DE LA LIGNE, deja habilles (« 3 h 30 »), dans l'ordre
   * declare : ce que le kanban ecrit sur sa carte. Absent sans total lie.
   */
  totauxLies?: string[];
  /** Role `subtitle`, deja mis en texte (ou le texte `empty` du champ). */
  subtitle?: string;
  /** Role `avatar` : URL, ou initiales, selon ce que l'ecran fournit. */
  avatar?: string;
  /** Role `couleur`, deja validee (`estCouleur`) : l'anneau de la pastille. Une valeur invalide est ecartee. */
  couleur?: Couleur;
  /** Role `photo` : l'id du fichier de la photo de reference. Vide : absent. */
  photo?: string;
  /**
   * LE JEU PORTE LA COULEUR OU LA PHOTO (un des deux roles est projete), meme
   * quand cette fiche n'a ni l'une ni l'autre. C'est ce qui fait dessiner une
   * pastille a TOUTES les lignes d'un jeu (`hasPastille`), jamais a celles qui
   * ont deja une couleur : la colonne des titres ne danse pas d'une ligne a
   * l'autre.
   */
  identite?: true;
  /** Role `status`, resolu dans le catalogue du tenant. */
  status?: Option;
  /** Role `badges`, resolu : les etiquettes secondaires (tags) de la ligne. */
  badges: Option[];
  /** Role `groupKey`, resolu : les colonnes de kanban de cette ligne. */
  groups: Option[];
  /** Role `lane`, resolu : les couloirs de calendrier de cette ligne. */
  lanes: Option[];
  /** Role `dateStart`. */
  start?: Date;
  /**
   * Present = `start` est un INSTANT (le role porte `FieldMap.instant`), a lire
   * dans ce fuseau ; absent = une date, ecrite au jour seul comme avant.
   */
  startFuseau?: string;
  /** Role `dateEnd`. */
  end?: Date;
  /** Roles libres, propres a une vue custom (ex. `extra.lat`). */
  extra: Record<string, Cell>;
  /**
   * Les colonnes : la valeur comparable, son rendu, et si elle est editable.
   * L'editabilite d'une colonne vit ICI, dans la cellule, JAMAIS dans
   * `editable` ci-dessous : les deux espaces de cles peuvent collisionner (une
   * colonne `status` n'est pas le role `status`), et le kanban decide du
   * glisser-deposer sur `editable['groupKey']`.
   */
  cells: Record<string, ProjectedCell>;
  /**
   * Un ROLE est editable SSI l'ecran a fourni `set()` dessus. Reserve aux
   * roles : une cle de colonne n'y entre jamais (cf. `cells`).
   */
  editable: Record<string, boolean>;
  /**
   * Fiche jamais ouverte sur ce poste depuis la date de reference de la porte.
   *
   * TOUJOURS PRESENTE, jamais optionnelle : une vue lit `row.isNew` sans garde,
   * exactement comme elle lit `row.badges`. Quand le reperage est eteint (pref
   * `app.<appId>.nouveau`), elle vaut `false` partout et le core ne calcule
   * rien. C'est le CORE qui la pose (voir `project.ts` et le memo `projected`
   * de `useScreen`) : une vue ne lit aucun contexte, elle ne recoit que des
   * props.
   */
  isNew: boolean;
  /**
   * LA SURBRILLANCE de la ligne (`ViewBinding.highlights`), `null` quand aucune
   * regle n'est vraie. TOUJOURS PRESENTE, comme `isNew` : une vue la lit sans
   * garde. La projection la calcule pour toutes les vues (c'est peu de chose),
   * seul le kanban la dessine.
   */
  highlight: RowHighlight | null;
}

/** Colonne telle que la vue la recoit : les metadonnees, sans la fonction d'acces. */
export interface ViewColumn {
  key: string;
  label: string;
  sortable: boolean;
  /** Le libelle du lien que cette lecture suit (`Column.lueSur`), recopie tel quel. */
  lueSur?: string;
  agg?: string;
  /** L'habillage de l'agregat (voir `Column.formatAgg`). */
  formatAgg?: (valeur: number) => string;
  /** Un total lie par ligne (`Column.totalLie`) : sans fiche liee, la case se tait. */
  totalLie?: true;
  /**
   * La colonne porte un `set()`. Requis, toujours calcule : une vue ne doit pas
   * avoir a deviner qu'un `undefined` veut dire "lecture seule".
   */
  editable: boolean;
  /** Type d'editeur declare par l'ecran. Absent : la fiche le deduit de la valeur. */
  kind?: EditorKind;
  /** Le type du champ (`Column.valueType`), recopie tel quel par `colonnesDeVue`. */
  valueType?: TypeDeChamp;
  /** Ses reglages d'affichage (`Column.reglages`), recopies tels quels. */
  reglages?: ReglagesDeColonne;
  /**
   * LE CHEMIN DE STOCKAGE de la colonne (`Column.path`), recopie tel quel par
   * `colonnesDeVue`. DE LA DONNEE, JAMAIS UNE FONCTION.
   *
   * ABSENT = la colonne est stockee sous sa clef, c'est-a-dire un champ NATIF
   * de la famille ; `customFields.<clef>` = un champ SUR-MESURE, dans le sac.
   *
   * POURQUOI IL TRAVERSE LA FRONTIERE : c'est la seule chose qui dise, cote
   * vue, si le champ SUPPORTE d'etre vide. Un patch a `null` ne supprime la
   * clef que sous `customFields` (`buildUpdateData`,
   * `packages/sdk/src/standard-fields.ts`), donc l'editeur `lien` ne propose
   * « Aucun » que sur un chemin du sac (`proposeDetacher`,
   * `views/core/RecordView.tsx`).
   */
  path?: string;
  /**
   * LE PLACEHOLDER DE LA CASE VIDE (`Column.placeholder`), recopie tel quel par
   * `colonnesDeVue`. La fiche le rend en creation, et l'editeur le pose sur son
   * `input` / `textarea`.
   */
  placeholder?: string;
  /**
   * LE CHAMP EST OBLIGATOIRE (`Column.required`), recopie tel quel. La fiche
   * suffixe le libelle par « obligatoire » sur un brouillon ; le moteur, lui,
   * s'en sert pour reclamer les cases vides avant de creer.
   */
  required?: boolean;
  /**
   * LA PORTE QUE CETTE COLONNE DESIGNE, quand elle porte l'id d'une fiche
   * d'ailleurs (`Column.reference`, deduite du sens inverse d'un lien).
   *
   * DE LA DONNEE, JAMAIS UNE FONCTION : `titleOf` et `subtitleOf` restent du
   * cote du core (ils lisent une fiche BRUTE et servent a remplir l'annuaire).
   * Ce qui traverse la frontiere, c'est de quoi RETROUVER l'annuaire du jeu
   * vise (`cleDeJeu(family, type)`) et nommer la porte ou la fiche liee vit.
   * C'est par elle que la fiche sait quel annuaire servir a un editeur `lien`.
   *
   * ABSENTE = la colonne porte une valeur, pas une relation. SANS `door`, la
   * relation vise une famille que l'ecran ne monte pas (voir
   * `ColumnReference.door`). `modeles` : la fiche visee est un modele, qui
   * s'ouvre dans `door` en mode Modeles (`ColumnReference.modeles`).
   */
  reference?: { door?: string; family: string; type?: string; modeles?: boolean };
}

/**
 * LA CIBLE d'une ecriture, telle que `saveState` la designe.
 *
 * L'etat d'enregistrement est celui de l'ECRAN (un seul indicateur, rangee 3),
 * mais deux ecritures peuvent etre en vol en meme temps : cliquer un champ,
 * enchainer sur un second sans attendre. Sans la cible, la reponse de la
 * PREMIERE serait attribuee a la case de la SECONDE (fausse coche sur un champ
 * qui n'a pas encore repondu, et le premier reste en attente pour toujours).
 * La cible voyage donc avec l'etat : chaque reponse designe SA case.
 */
export interface SaveTarget {
  /** Role ou colonne : deux espaces de cles distincts, jamais melanges. */
  kind: 'role' | 'column';
  /** L'enregistrement ecrit. Une fiche ne reagit qu'a ce qui la concerne. */
  id: string;
  /** Nom du role, ou cle de la colonne. */
  key: string;
}

/**
 * L'ETAT D'ENREGISTREMENT d'un ecran, rendu dans la rangee 3 du gabarit.
 *
 * Un seul etat pour TOUTES les ecritures de l'ecran (roles et colonnes) : c'est
 * l'ecran qui enregistre, pas le champ. `idle` ne montre rien du tout, sinon la
 * rangee porterait un encombrement avant meme la premiere ecriture.
 *
 * Il vit ici, dans le contrat, et non dans `useScreen` : `ScreenLayout` importe
 * deja ce fichier en type-only, l'inverse n'est pas vrai.
 */
export interface SaveState {
  mode: 'idle' | 'saving' | 'saved' | 'error';
  /**
   * L'heure de la derniere ecriture reussie. L'indicateur de la rangee 3 ne la
   * montre plus ("Enregistre" tout court, sans heure) : elle reste dans l'etat.
   */
  at?: Date;
  /**
   * CE QUE CET ETAT CONCERNE. Absent en `idle` (il n'y a rien a designer). La
   * rangee 3 n'en fait rien (elle ne rend qu'un mode) ; la FICHE, elle, s'en sert
   * pour rendre la coche ou le cadre rouge a la bonne case, meme quand deux
   * ecritures se chevauchent.
   */
  target?: SaveTarget;
  /**
   * LA RAISON D'UN REFUS, JAMAIS UNE PANNE.
   *
   * Elle n'accompagne qu'un `mode: 'error'`, et elle change ce que la pastille
   * dit : la phrase du refus REMPLACE « Echec de l'enregistrement » et RETIRE le
   * bouton « Reessayer », qui rejouerait un refus (meme rendu que la lecture
   * seule, `SaveIndicator`, `components/ScreenLayout.tsx`). Un echec ORDINAIRE
   * ne la porte pas : il garde sa relance, parce qu'il peut reussir au coup
   * suivant.
   *
   * D'OU ELLE VIENT, et il y a deux sources. Du MOTEUR, quand il refuse
   * lui-meme : la lecture seule, ou la suppression d'une fiche qui tient des
   * fiches liees, refusee avec son compte (`refusDeSuppression`,
   * `packages/ui/src/lib/liens.tsx`). Et de l'APP, quand elle lance `refuser()`
   * depuis un `set()` ou depuis un geste de fiche : une adresse mal formee, un
   * nom qu'on ne peut pas vider (`views/refus.ts`).
   *
   * LE MESSAGE D'UNE PANNE NE PASSE JAMAIS PAR ICI, et c'est tout l'objet du
   * marqueur que `refuser()` pose : « Missing or insufficient permissions » ou
   * la liste JSON d'un ZodError sur la pastille d'un ecran francais ne vaut pas
   * mieux que « Echec de l'enregistrement ». Seule une phrase ecrite POUR ETRE
   * LUE remplace la phrase du socle.
   *
   * Le refus n'ouvre AUCUNE fenetre de plus : il se dit dans la pastille qui
   * existe deja.
   */
  reason?: string;
}

/** Etat de chargement de la zone 5, tenu par le core et rendu par le ViewHost. */
export interface ViewStatus {
  /** Premier chargement en cours (donnees encore vides). */
  loading: boolean;
  /** Le chargement a echoue. */
  error?: Error;
  /** Aucune donnee n'existe (PAS "aucun resultat de recherche"). */
  empty: boolean;
  /** Le plafond de fenetre est atteint : il existe des lignes non affichees. */
  truncated: boolean;
}

/**
 * UN NIVEAU DE TRI de la pile de l'ecran (gabarit v2). La cle est une cle de
 * colonne ou de role, la meme que celle du tri simple historique : c'est ce qui
 * permet aux deux ecritures (pile et miroir `sort`) de partager `sortValueOf`.
 */
export interface SortLevel {
  key: string;
  dir: 'asc' | 'desc';
}

/**
 * UN FAVORI (zone 4c du gabarit v2) : un etat d'ecran {filtres, pile de tri}
 * NOMME. Ce n'est jamais une donnee metier, seulement une combinaison de
 * filtres et de tris reels. Deux provenances, meme forme : les presets
 * declares par la porte (`ScreenDoor.presets`) et les favoris gardes par
 * l'utilisateur (pref locale, une liste par app+ecran+porte).
 */
export interface ScreenFavorite {
  /** Id stable. Les favoris utilisateur prennent `user-<n>`, jamais un id de preset. */
  id: string;
  /** Libelle affiche sur la pastille (zone 4c). */
  label: string;
  /** Les ids de filtres actifs (catalogue de la porte), sans ordre significatif. */
  filterIds: string[];
  /** La pile de tri, ORDONNEE : le niveau 1 commande le niveau 2. */
  sortStack: SortLevel[];
}

/**
 * LA REQUETE, propriete de l'ECRAN, tenue par le core (`useScreen().query`).
 *
 * Une seule requete pour toutes les vues d'un ecran : changer de vue n'est
 * qu'un setState, donc la recherche, le tri et la fenetre survivent par
 * construction. Aucune vue ne refiltre, ne retrie, ne recharge.
 *
 * DEUX PAIRES DE CHAMPS RACONTENT LA MEME CHOSE, et ce n'est pas un accident :
 * `filterIds` / `sortStack` sont l'etat CANONIQUE du gabarit v2 (multi-filtres
 * additifs, tri en pile), `filterId` / `sort` en sont les MIROIRS de
 * compatibilite (la lecture V1 : une pastille unique, un tri simple). Les
 * miroirs ne sont jamais ecrits a la main : toute mutation passe par
 * `nextQuery` (useScreen.ts), qui tient les deux paires alignees quel que soit
 * le champ patche. Un ecran V1 qui lit `query.filterId` ou une vue qui patche
 * `query.sort` (l'en-tete du tableau) continuent donc de fonctionner tels
 * quels, pendant que les tags de la zone 4 lisent l'etat complet.
 */
export interface ScreenQuery {
  /** Texte de la recherche (zone 2b). Applique par le core sur les lignes projetees. */
  search: string;
  /**
   * Les FILTRES ACTIFS (gabarit v2) : des ids du catalogue de la porte,
   * ADDITIFS (une ligne passe si AU MOINS UN filtre la retient : un OU, jamais
   * un ET). Liste vide = aucun filtrage ("Tout le parc"). L'ordre est l'ordre
   * de pose (celui des tags de la zone 4a). C'est cette liste qui voyage dans
   * le parametre `f` de l'adresse, en ids separes par des virgules.
   */
  filterIds: string[];
  /**
   * MIROIR V1 de `filterIds` : l'id quand EXACTEMENT UN filtre est actif,
   * null sinon. C'est ce que lisent les ecrans d'avant les portes (pastille
   * unique). Ne jamais l'ecrire directement : `nextQuery` le derive.
   */
  filterId: string | null;
  /**
   * Id de la SECTION active (l'entree de menu de la zone 1), ou null quand
   * l'ecran n'a pas de menu. Elle SEGMENTE les donnees : c'est au chargeur de
   * l'honorer (`load(query)` la recoit), le core ne sait pas ce qu'elle veut
   * dire. Elle vit dans la requete et non a cote parce qu'elle a exactement le
   * meme effet qu'un `range` ou un `after` : la changer PERIME les pages deja
   * empilees en mode serveur.
   */
  section: string | null;
  /**
   * LA PILE DE TRI (gabarit v2), ORDONNEE : le niveau 1 commande, les suivants
   * departagent ses egalites. Pile vide = l'ordre rendu par le chargeur
   * ("Ordre d'origine"). Elle n'entre JAMAIS dans l'adresse (meme decision que
   * la recherche : un lien porte des ids, pas la maniere de regarder).
   */
  sortStack: SortLevel[];
  /**
   * MIROIR V1 de `sortStack` : son PREMIER niveau, ou null. Deux consommateurs
   * legitimes : l'en-tete du tableau (aria-sort, clic = patch de `sort`, que
   * `nextQuery` renormalise en pile d'un seul niveau) et la pagination serveur
   * (le SDK ne prend qu'UN `orderBy` : le niveau 1 part au serveur, les
   * suivants raffinent en memoire sur les pages chargees). Ne jamais l'ecrire
   * a la main : `nextQuery` le derive.
   */
  sort: SortLevel | null;
  /** Role de regroupement demande par la vue (le kanban regroupe par `groupKey`). */
  groupBy: string | null;
  /**
   * Taille de la fenetre courante. Lot 1 : plafond CLIENT (le SDK ne pagine
   * pas encore), au-dela le ViewHost affiche "Affinez la recherche".
   */
  limit: number;
  /** Curseur de pagination serveur (id du dernier enregistrement lu). Lot 2. */
  after: string | null;
  /** Intervalle de dates demande par le calendrier. Lot 3. */
  range: { from: Date; to: Date } | null;
  /** Inclure les enregistrements archives (`archivedAt` non nul). Defaut false. */
  includeArchived: boolean;
  /**
   * LE MODE ARCHIVES est-il allume (chantier 2_0). Defaut false.
   *
   * Ce n'est PAS un filtre de plus, et c'est tout le point : le mode decide
   * du JEU, son test passe AVANT les filtres. Allume, l'ecran montre les
   * fiches archivees et RIEN d'autre ; eteint, on n'en voit aucune. Les
   * filtres, la recherche et le tri tranchent ensuite ce jeu exactement comme
   * le travail courant (decision du fondateur du 28/09/2026).
   *
   * A NE PAS CONFONDRE avec `includeArchived` juste au-dessus, qui dit
   * "montre les archives EN PLUS des actifs" (l'equivalent du `'all'` du SDK)
   * et que ce chantier n'a pas touche. Les deux champs coexistent :
   * `archivesMode` gagne quand il est allume, puisqu'il retire les actifs.
   *
   * ET C'EST POUR CA QU'IL S'APPELLE `archivesMode` ET PAS `archives` : deux
   * champs VOISINS qui parlent des memes documents avec des sens OPPOSES se
   * confondent a la lecture rapide, et une confusion ici ne fait pas planter
   * le build, elle rend la mauvaise liste. Le suffixe `Mode` dit le fait
   * exact : ce n'est pas une inclusion, c'est un ETAT A PART de l'ecran. Il
   * porte deja ce nom au premier niveau de l'etat rendu par `useScreen` : un
   * seul fait, un seul mot.
   *
   * IL VOYAGE JUSQU'AU CHARGEUR, comme `section` et `filterIds` : c'est
   * l'ECRAN qui appelle son `load`, donc c'est lui qui traduit ce booleen en
   * option de SDK (`list({ archived })`). Deux traductions legitimes, et le
   * choix n'est pas cosmetique :
   * - pagination CLIENTE (le chargeur rend tout) : relayer `'all'` et laisser
   *   le core faire le partage. C'est ce que font les deux ecrans de
   *   reference, et c'est ce qui garde les compteurs et les stats VRAIS
   *   (regle G15 : ils portent sur la porte BRUTE, donc sur les actifs). Avec
   *   `'only'`, les actifs quittent la memoire et toute stat calculee dessus
   *   tomberait a zero le temps du mode ;
   * - pagination SERVEUR : relayer `'only'`, parce qu'il n'est pas question
   *   de ramener les actifs page par page pour les jeter. Les stats clientes
   *   d'un ecran serveur ne decrivaient deja que les pages chargees.
   *
   * CE QU'ON NE FAIT JAMAIS, dans aucun des deux cas : appeler
   * `count({ archived: 'only' })` ou `'all'`. `count()` est une agregation
   * serveur, elle ne lit aucun document, donc elle ne peut pas retirer les
   * SUPPRIMES que `runList` retire cote client apres lecture : le compteur
   * depasserait la liste d'exactement le nombre de supprimes (la raison
   * complete est en tete de `packages/sdk/src/list-query.ts`).
   */
  archivesMode: boolean;
  /**
   * LE MODE MODELES est-il allume. Defaut false.
   *
   * Le JUMEAU du mode Archives, et il se lit comme lui : ce n'est pas un filtre
   * de plus, c'est un ETAT A PART de l'ecran qui decide SEUL du jeu. Allume, la
   * liste montre les MODELES de la porte (et rien d'autre), qu'on peut y voir,
   * MODIFIER et completer. Les filtres, la recherche et le tri le tranchent
   * ensuite comme n'importe quel jeu.
   *
   * DEUX DIFFERENCES AVEC SON JUMEAU, ET ELLES COMPTENT TOUTES LES DEUX :
   *
   * 1. IL NE VOYAGE PAS JUSQU'AU CHARGEUR. `archivesMode` est un parametre de
   *    REQUETE : l'ecran le traduit en `list({ archived })`. Celui-ci ne l'est
   *    pas : les modeles ne vivent pas dans la collection de l'app, ils vivent
   *    dans le repository des modeles, que le SOCLE lit par `ModelesPort`.
   *    Aucune app n'a donc une ligne a ajouter, aucun `load` n'a a le
   *    connaitre, et il n'entre ni dans `loadKey` ni dans la liste de
   *    peremption de `nextQuery` (`views/useScreen.ts`) : un mode qui
   *    perimerait les pages du chargeur pour un jeu que le chargeur ne rend
   *    pas rechargerait la porte pour rien.
   * 2. IL EST EXCLUSIF D'`archivesMode`. Les deux decident du jeu affiche :
   *    allumes ensemble, ils diraient deux listes a la fois. Allumer l'un
   *    eteint l'autre, dans le MEME patch (`clearAll()`, lui, n'en eteint
   *    aucun : il vide la tranche, pas le jeu). Dans l'adresse, `arch` gagne sur `mod` (voir `readModesParams`,
   *    `views/screenUrl.tsx`) : les liens `arch=1` deja partages ne changent
   *    pas de sens.
   */
  modelesMode: boolean;
  /**
   * LA PORTE EST OUVERTE DANS UNE FICHE (`ScreenDoor.auClic`) : la fiche
   * parente, sa porte et le lien par lequel on est entre. Sur la porte liee,
   * le moteur ne garde que les fiches qui designent le parent ; il est dans la
   * clef de chargement, et dans l'adresse (`dans`). Absent ou `null` : la porte
   * montre tout son jeu, comme avant. Le detail : `docs/socle/porte-fiche.md`.
   */
  dans?: { porte: string; lien: string; id: string } | null;
}

/**
 * LE CURSEUR DE FICHE : la liste ordonnee d'ids, GELEE a l'ouverture.
 *
 * "3 / 47" ne bouge pas sous les doigts. Les filtres et le tri restent actifs
 * et pilotent le PROCHAIN curseur, pas celui en cours. Objet de donnees pur
 * (aucune methode) : la vue navigue en appelant `onOpenRecord(cursor.nextId)`.
 */
export interface Cursor {
  /** La liste gelee. Vide tant que rien n'est ouvert. */
  ids: string[];
  /** L'element ouvert, null hors fiche. */
  openId: string | null;
  /** Rang 0-based dans `ids`. -1 : rang non resolu (entree a froid). */
  index: number;
  /** Le "47" de "3 / 47". */
  total: number;
  /** Le rang est resolu : compteur et fleches actifs. Faux = entree a froid. */
  ready: boolean;
  /**
   * L'element ouvert n'appartient plus au jeu ordonne courant (filtre, tri,
   * recherche). On ne substitue JAMAIS en silence : on le garde affiche, avec
   * la mention "hors du filtre courant".
   */
  outOfScope: boolean;
  hasPrev: boolean;
  hasNext: boolean;
  /** Id precedent, null en tete de liste ou si le rang n'est pas resolu. */
  prevId: string | null;
  /** Id suivant, null en fin de liste ou si le rang n'est pas resolu. */
  nextId: string | null;
}

/* ------------------------------------------------------------------ */
/* Ce que la VUE recoit                                                */
/* ------------------------------------------------------------------ */

/**
 * LES SEULES ENTREES D'UNE VUE. Rien d'autre : aucun contexte React, aucun
 * SDK, aucun routeur. Une vue qui a besoin de plus n'est pas une vue, c'est
 * un ecran.
 */
export interface ViewProps {
  /** La fenetre courante, deja projetee, filtree, triee, archives exclus. */
  rows: ProjectedRow[];
  /** Les colonnes declarees par le binding de CETTE vue. */
  columns: ViewColumn[];
  /** Catalogue d'un role catalogue : celui du tenant, plus les valeurs hors catalogue rencontrees. */
  optionsOf(role: string): Option[];
  /** Chargement, erreur, vide, tronque. Rendu par le ViewHost, lisible par la vue. */
  status: ViewStatus;
  /** La requete courante (lecture seule). */
  query: ScreenQuery;
  /** Demander une modification de la requete (tri au clic sur un en-tete, ...). */
  onQueryChange(patch: Partial<ScreenQuery>): void;
  /** Le curseur de fiche. */
  cursor: Cursor;
  /** Ouvrir un element (bascule en niveau `record`), ou fermer avec null. */
  onOpenRecord(id: string | null): void;
  /**
   * LES LIGNES NE S'OUVRENT PAS : la vue les rend sans clic, sans main au
   * survol, sans fond de survol et hors de l'ordre de tabulation. ABSENT PARTOUT
   * SAUF dans l'APERCU d'une carte liee, sur un brouillon issu d'un modele
   * (23/09/2026) : ces lignes sont les enfants du MODELE, les ouvrir menerait a
   * le modifier depuis une fiche qui n'en est pas un. Une ligne qui promet un
   * clic et ne fait rien ment (G16) : elle ne le promet donc pas.
   *
   * LU PAR LES DEUX VUES QU'UNE CARTE LIEE MONTE (`core.tableau`, `core.liste`).
   * Les autres l'ignorent, et c'est sans consequence : aucune n'est montee la.
   */
  lignesInertes?: boolean;
  /**
   * OUVRIR UNE FICHE D'UNE AUTRE PORTE, depuis une carte liee : on CHANGE de
   * porte, l'adresse devient `?s=<porte>&id=<id>` et le retour navigateur
   * ramene au parent. Absente hors du niveau `record` (et chez un hote qui ne
   * la fournit pas) : une vue custom n'en a pas besoin, et `onOpenRecord`
   * ouvrirait la fiche liee sous la porte PARENTE, donc introuvable.
   *
   * SANS `id`, ON PASSE DANS LA PORTE, PAS DANS UNE FICHE : l'adresse devient
   * `?s=<porte>` (la collection), toujours en push. C'est le trajet du
   * « + Ajouter » d'une carte liee, qui va CREER la-bas : il n'y a pas encore
   * de fiche a ouvrir, et l'etape du parent doit rester derriere pour que le
   * retour navigateur y ramene.
   */
  onOpenLinked?(door: string, id?: string, options?: OptionsDeSaut): void;
  /**
   * OU MENE UNE REFERENCE SANS PORTE : la fiche visee vit dans une AUTRE app,
   * ou nulle part d'accessible.
   *
   * Le core la demande au shell (`NavigationPort`, `lib/navigation.tsx`), qui
   * seul sait ce qui est installe et accessible : une app ne connait pas les
   * autres. Rend le LIBELLE de l'app qui portera la fiche, et le geste qui y
   * va ; `null` quand aucune app listee ne montre cette famille, et la fleche
   * ne se rend alors pas du tout (G16 : pas de geste qui ne mene nulle part).
   * Le nom, lui, s'affiche toujours : l'annuaire ne depend pas de la porte.
   *
   * Servie au niveau FICHE uniquement, comme `onOpenLinked`, et pour la meme
   * raison.
   */
  destinationDeReference?(cible: {
    family: string;
    type?: string;
    id: string;
  }): { libelle: string; ouvrir(): void } | null;
  /**
   * L'ECRAN EST EN LECTURE SEULE, ET VOICI POURQUOI (`ScreenState.raisonAcces`).
   *
   * ABSENTE = ON ECRIT. Presente, elle porte la RAISON, et c'est elle qui se
   * met en infobulle : un controle eteint DIT pourquoi (G16). Les vues n'ont
   * rien a en deduire sur les cases (le core a deja retire les `set` des lignes
   * qu'il projette) ; elle sert aux gestes qu'une vue fabrique elle-meme, et
   * il n'y en a qu'un : le « + Ajouter » d'une carte liee, qui irait CREER
   * dans une autre porte.
   */
  lectureSeule?: string;
  /**
   * LE TABLEAU S'EDITE EN PLACE (`ScreenDoor.modifierDansLeTableau`). Lu par
   * `core.tableau` seul, et present SEULEMENT si la porte active l'a allume :
   * absent, le tableau se lit et un clic sur la ligne ouvre la fiche.
   */
  modifierDansLeTableau?: boolean;
  /** Ecrire un ROLE. Le core applique l'optimisme et remet en place en cas d'echec. */
  onPatchRecord(id: string, role: string, value: Cell): Promise<void>;
  /**
   * Ecrire une COLONNE. Meme optimisme, meme rollback, meme `saveState`, meme
   * pile d'annulation. Deux entrees distinctes plutot qu'une fourre-tout : les
   * espaces de cles (roles / colonnes) peuvent collisionner.
   */
  onPatchColumn(id: string, key: string, value: Cell): Promise<void>;
  /**
   * L'etat d'enregistrement de l'ECRAN. Une vue ne peut pas le deduire :
   * `onPatchRecord` et `onPatchColumn` ne rejettent jamais (le core avale
   * l'echec pour que le kanban puisse faire `void onPatchRecord(...)`), donc
   * c'est la seule facon pour un champ de savoir que son ecriture a echoue.
   */
  saveState: SaveState;
  /** Agrandir la fenetre. Absent si le chargeur ne peut rien donner de plus. */
  onLoadMore?(): void;
  /** Changer d'intervalle de dates (calendrier). Absent si la vue ne le demande pas. */
  onRangeChange?(from: Date, to: Date): void;
  /** Etat JETABLE de la vue (largeurs de colonnes, zoom), cloisonne par `viewId`. */
  viewState: unknown;
  setViewState(state: unknown): void;
  /**
   * La cle de pref des LARGEURS DE COLONNES de cette vue, deja composee par le
   * core (`columnWidthsPrefKey` : app, ecran, porte, vue). La vue y range son
   * objet `{ [cleDeColonne]: { rem, cadreRem } }` avec `readPref` / `writePref`.
   *
   * La valeur rangee est une PROPORTION DU CADRE, jamais une largeur de TABLE :
   * chaque largeur voyage avec le cadre ou elle a ete tiree, c'est le cadre
   * courant qui commande, et les colonnes non reglees se partagent ce qui reste
   * (`views/core/columnWidths.ts`).
   *
   * Elle est distincte de `viewState` a dessein : `viewState` est JETABLE (il
   * meurt avec le montage), alors qu'une largeur reglee a la main doit survivre
   * au changement de vue, au changement de porte et au rechargement de l'onglet.
   * Une vue qui ne regle aucune largeur l'ignore simplement.
   */
  columnWidthsKey: string;
  /** La zone 5, pretee par ScreenLayout : la barre de defilement du gabarit. */
  scrollRef: RefObject<HTMLElement | null>;
  /** Le sac libre du binding (`ViewBinding.options`), lu par cette seule vue. */
  options: Record<string, unknown>;
  /**
   * LES ANNUAIRES CHARGES, par clef de jeu : de quoi nommer une fiche que la
   * ligne DESIGNE. Absents chez un hote sans port de liens, et sur toute vue
   * dont aucune colonne n'est une reference (les lignes portent alors des ids
   * nus, comme avant ce lot).
   */
  annuaires?: Annuaires;
  /**
   * LES LIGNES CHOISIES (chantier 3_0), dans l'ordre ou l'utilisateur les a
   * prises, et de quoi changer ce choix.
   *
   * LES DEUX SONT ABSENTS DES QU'ON N'EST PAS EN TABLEAU, et c'est le coeur du
   * chantier, pas une limite : agir sur un lot demande de VOIR les valeurs sur
   * lesquelles on agit, et le tableau est la seule vue qui les montre toutes.
   * Choisir douze fiches dans un kanban, c'est choisir douze titres. La liste,
   * le kanban, le calendrier et la fiche ne les recoivent donc jamais, et une
   * vue qui les recoit sait, par leur seule presence, qu'elle a le droit de
   * dessiner sa colonne de choix.
   *
   * DEUX GARANTIES QUE LE CORE TIENT, ET QU'AUCUNE VUE N'A A REFAIRE :
   * - `selection` ne contient QUE des ids de `rows`. Une ligne qui sort de
   *   l'ecran (filtre, recherche, rechargement) sort de la selection dans le
   *   MEME rendu : une selection invisible est une bombe, on croit agir sur
   *   trois fiches et on en modifie douze ;
   * - changer de porte, de recherche, de filtre, de mode Archives ou de VUE la
   *   vide entierement.
   *
   * La vue rend `onSelectionChange` la liste COMPLETE voulue, jamais un delta :
   * c'est le core qui tient l'etat, comme pour la requete.
   */
  selection?: string[];
  onSelectionChange?(ids: string[]): void;
  /**
   * LA FICHE OUVERTE EST ARCHIVEE (niveau `record` uniquement, absent au
   * niveau collection : une collection n'a pas d'etat d'archive).
   *
   * C'est ce qui permet a la fiche de le DIRE. Sans ce bandeau, on modifie une
   * fiche sans savoir qu'elle est sortie des listes, et on s'etonne ensuite de
   * ne plus la retrouver. Le core le calcule (`archivedAt` non nul), la vue le
   * rend : elle ne lit jamais le champ elle-meme.
   */
  archived?: boolean;
  /**
   * DESARCHIVER LA FICHE OUVERTE, depuis le bandeau. Absent quand la fiche
   * n'est pas archivee, ou quand la porte ne declare pas le verbe
   * `unarchive` : le bandeau ne porte alors pas de bouton, plutot qu'un bouton
   * mort.
   *
   * L'endroit ou l'on APPREND l'etat est l'endroit ou l'on peut le QUITTER :
   * c'est la seule raison pour laquelle cette sortie double celle du menu de
   * la rangee 3c. Aucune fenetre de confirmation : desarchiver est reversible
   * et sans consequence, redemander serait de la ceremonie.
   */
  onUnarchive?(): void;
  /**
   * LA FICHE AFFICHEE EST UN BROUILLON DE CREATION (`~nouveau`), ABSENTE
   * PARTOUT AILLEURS. Sa seule presence dit a la fiche qu'elle rend une fiche
   * qui n'existe pas encore : les cases vides invitent a ecrire au lieu
   * d'annoncer « Non renseigne », le titre montre son placeholder, les cartes
   * liees disent « Disponible apres la creation. » (sauf quand `modele` est
   * pose : elles montrent alors les fiches liees du modele, en apercu).
   *
   * `manquants` : les cases obligatoires laissees vides au dernier « Creer »
   * refuse, dans la convention de la fiche (`fieldKeyOf` : `role:title`,
   * `col:<clef>`). Elles se cerclent de rouge jusqu'a ce qu'elles soient
   * remplies (le moteur retire la clef a l'ecriture). VIDE tant que personne
   * n'a essaye de creer : on ne rougit pas une fiche qu'on vient d'ouvrir.
   *
   * `placeholderTitre` : ce que le `h2` affiche a la place d'un titre vide.
   * Absent : la fiche retombe sur son defaut (« Titre de la fiche »).
   */
  brouillon?: {
    manquants: readonly string[];
    placeholderTitre?: string;
    /**
     * LE BROUILLON VIENT D'UN MODELE, choisi dans le menu de « Ajouter »
     * (23/09/2026) : ses fiches liees, telles que le modele les emporte. Les
     * cartes liees les montrent EN APERCU, en lecture (ni ouverture, ni ajout,
     * ni rattachement : ce sont celles du MODELE, les toucher le modifierait),
     * et les champs de total les comptent en memoire. Elles seront creees avec
     * la fiche au clic sur « Creer ». Absent (fiche vide ordinaire, ou modele
     * qui n'emporte aucune fiche liee) : les cartes disent « Disponible apres la
     * creation. » et les totaux « 0 », comme avant.
     *
     * CE N'EST PAS `ViewProps.modele`, et les deux ne se confondent jamais :
     * celui-la dit que la fiche affichee EST un modele (ses references perdent
     * leur fleche, ses cartes ouvrent et ajoutent des enfants de modele). Ici la
     * fiche affichee est une fiche a creer, qui a seulement un modele d'origine.
     */
    modele?: ModeleOuvert;
    /**
     * L'ANCIENNE PLACE DE `ViewProps.verrouillees`, ENCORE LUE. Elle a demenage
     * au premier niveau le 10/09/2026, quand la fiche d'un ENFANT de modele a
     * eu besoin de la meme chose sans etre un brouillon. La fiche lit les deux
     * (l'union), pour qu'une surface qui poserait encore celle-ci ne perde pas
     * ses cases fermees ; le moteur, lui, ne pose plus que celle du dessus.
     */
    verrouillees?: readonly string[];
  };
  /**
   * LES CASES QUE LE GESTE A REMPLIES ET QUE L'UTILISATEUR NE CHANGE PAS
   * (`fieldKeyOf` : `role:title`, `col:<clef>`), niveau fiche seulement. La
   * fiche les rend SANS editeur, comme une carte verrouillee : c'est le geste
   * qui les a posees, pas une saisie.
   *
   * DEUX GESTES LES POSENT, ET C'EST POURQUOI ELLES VIVENT ICI ET PLUS SOUS
   * `brouillon` :
   *  - le « + Ajouter » d'une CARTE LIEE, qui impose le champ du lien sur la
   *    fiche vide qu'il ouvre (« Materiel : Sony FX9 n°1 ») ;
   *  - la fiche d'un ENFANT de modele, dont le champ du lien n'est stocke NULLE
   *    PART (un patron ne retient aucun rattachement) : la case affiche le nom
   *    du modele parent, pose par le moteur, et ne s'ouvre pas. Sans ce
   *    verrou, elle disait « Materiel : Non renseigne » et proposait de choisir
   *    une fiche, ce qu'un enfant ne peut pas porter.
   *
   * LE MOT AFFICHE NE VIENT PAS D'ICI : c'est le moteur qui le pose sur la
   * CELLULE (`applyColumnPatch`, le meme chemin que le libelle d'une valeur
   * imposee). Cette liste ne dit que « fermee ».
   */
  verrouillees?: readonly string[];
  /**
   * LA FICHE AFFICHEE EST UN MODELE (mode Modeles), niveau fiche seulement,
   * ABSENTE partout ailleurs. Sa presence dit a la fiche que ses cartes liees
   * ne montrent pas des fiches du serveur mais les ENFANTS du modele, et que
   * ses champs de total se calculent sur ces enfants (voir `ModeleOuvert`).
   * Jamais posee avec `brouillon` : un brouillon ne vit dans aucun mode.
   *
   * ELLE NE DONNE AUCUN VERBE, depuis le pivot du 10/09/2026 : la carte LIT.
   * Ouvrir une ligne, en ajouter une, en retirer une sont les gestes d'une
   * FICHE, et ils passent par les chemins de fiche (`onOpenLinked`, l'intention
   * de creation, le menu « ... » de l'enfant ouvert).
   */
  modele?: ModeleOuvert;
  /**
   * LE CONTENU DE LA CARTE DES FICHIERS JOINTS, niveau fiche seulement, fourni
   * par l'HOTE (`ViewHostProps.fichiers`) : la fiche pose la carte a son rang,
   * pleine largeur, et l'hote la remplit, parce que lui seul sait lire la
   * capacite du jeu (`jeux[].fichiers`) et parler au SDK.
   *
   * `null` : la carte N'APPARAIT PAS (le jeu n'ouvre pas les fichiers). Prop
   * ABSENTE (l'apercu du Studio, un banc d'essai) : la fiche rend un apercu
   * muet, le titre et une phrase, sans aucun geste.
   */
  fichiers?: (carte: CarteFichiers) => ReactNode | null;
  /**
   * LA FAMILLE DU JEU de la fiche ouverte (`contacts`, `assets`), niveau fiche
   * seulement, fournie par l'HOTE (`ViewHostProps.collection`) : de quoi ranger la
   * photo de reference avec la fiche (`PhotosDeFiches.envoyer`). ABSENTE : pas de
   * geste « photo » sur la pastille, la fiche ne sait pas ou ranger le fichier.
   */
  collection?: string;
}

/**
 * LE MODULE D'UNE VUE : ce que `ViewType.load()` rend.
 *
 * Le composant de rendu ET son squelette voyagent ENSEMBLE, dans le meme module
 * paresseux. C'est ce qui permet au squelette d'appartenir vraiment a la vue
 * sans payer son import au chargement de l'ecran : un descripteur qui importerait
 * son squelette en statique tirerait tout le fichier de la vue avec lui, et
 * `load()` ne serait plus paresseux du tout.
 *
 * Un module sans `Skeleton` est legitime : le `ViewHost` retombe alors sur le
 * squelette de liste du gabarit (`SkeletonListRows`), la bonne forme pour toute
 * vue au pattern de reference.
 */
export interface ViewModule {
  /** Le composant de rendu. Export nomme `Component`. */
  Component: ComponentType<ViewProps>;
  /** Le squelette de CETTE vue, aux memes rem que son rendu. Export nomme `Skeleton`. */
  Skeleton?: ComponentType;
}

/**
 * LE DESCRIPTEUR D'UN TYPE DE VUE : sa carte d'identite, cote a cote avec son
 * chargeur paresseux. C'est ce que l'ecran importe explicitement et passe a
 * `useScreen({ types })`. Les vues ne s'auto-enregistrent JAMAIS : sans import
 * explicite, le build les supprimerait.
 */
export interface ViewType {
  /** Id namespace (voir `ViewId`). */
  id: ViewId;
  /** Libelle francais, affiche dans le selecteur. */
  label: string;
  /** Icone du selecteur, prise dans le registre @taki/ui (jamais un SVG maison). */
  icon: ReactNode;
  /** Niveau : `collection` ou `record` (la fiche). */
  level: ViewLevel;
  /**
   * `false` : cette vue de FICHE n'est JAMAIS la vue d'arrivee d'une fiche (ni
   * le choix de « Ouvrir les fiches sur », ni la derniere vue utilisee, ni le
   * defaut de la porte, ni la premiere du catalogue). Elle reste proposee par le
   * selecteur : seul le clic sur son entree, ou une adresse qui la nomme
   * (`view=`), l'ouvre. Absent : une vue d'arrivee possible comme les autres.
   * Sans effet sur une vue de collection.
   */
  arrivee?: false;
  /**
   * Ce que la vue EXIGE du binding pour etre proposee. Chaque entree est un
   * nom de role (`FieldRole` ou role libre). Un role non satisfait grise
   * l'entree du selecteur avec sa raison au survol, jamais ne la supprime.
   *
   * Pseudo-role reserve : `'columns'` exige au moins une `Column` declaree
   * (le tableau n'a pas de role a exiger, il a besoin de colonnes).
   */
  requires?: string[];
  /** Roles propres a cette vue (ex. 'lat', 'lng') : ils arrivent dans `row.extra`. */
  extraRoles?: string[];
  /**
   * Ce que la vue exige du CHARGEUR. Declare honnetement des maintenant, meme
   * si le SDK ne pagine pas encore : c'est ce qui evitera de rouvrir les cinq
   * vues le jour ou la pagination serveur arrive.
   * `page` = une page de resultats ; `window` = une fenetre mobile (defilement,
   * intervalle de dates) ; `all` = tout le jeu (le kanban doit voir chaque
   * carte pour la poser dans sa colonne).
   */
  demand: 'page' | 'window' | 'all';
  /**
   * Qui possede la barre de defilement. `zone` = celle du gabarit (zone 5) ;
   * `self` = la vue gere son propre defilement dans une boite a hauteur fixe
   * (tableau a colonne gelee, kanban a colonnes independantes, calendrier).
   */
  scroll: 'zone' | 'self';
  /**
   * LA VUE NE DESSINE QUE CE QUI EST VISIBLE (fenetre virtuelle,
   * `views/core/fenetreVirtuelle.ts`). Le core lui passe alors TOUT le jeu
   * projete, sans plafond d'affichage ni bandeau « Afficher plus » : on le
   * parcourt en defilant. Absent, la vue recoit la fenetre plafonnee
   * (`DEFAULT_LIMIT`) et le bandeau dit ce qui manque. Chantier `ecran-fluide`.
   */
  virtuelle?: true;
  /**
   * Chargement paresseux du module de la vue : `Component`, et `Skeleton` s'il
   * en a un. C'est de LA que le `ViewHost` lit le squelette une fois le module
   * charge.
   */
  load(): Promise<ViewModule>;
  /**
   * Squelette declare en DUR sur le descripteur, donc disponible avant meme que
   * `load()` ait fini. A reserver aux cas ou le tout premier affichage compte
   * plus que le poids de l'import : il prime sur celui du module. Sinon, laisser
   * le squelette dans la vue et l'exporter sous le nom `Skeleton`.
   */
  Skeleton?: ComponentType;
  /**
   * Actions que la vue propose a la rangee 3a. SEULE porte d'une vue vers la
   * rangee d'actions : elles sont rendues par le core, TOUJOURS en secondaire.
   * Le bouton lime unique reste la propriete de l'ecran.
   */
  actions?(ctx: ViewProps): ScreenAction[];
}

/* ------------------------------------------------------------------ */
/* Ce que le SELECTEUR recoit                                          */
/* ------------------------------------------------------------------ */

/**
 * Une entree du selecteur de vues (zone 3c, groupe epingle a droite).
 * Calculee par `useScreen` en confrontant `ViewType.requires` au binding de
 * l'ecran : une entree non satisfaite est GRISEE avec sa raison, jamais
 * absente.
 */
export interface ViewOption {
  id: ViewId;
  label: string;
  icon: ReactNode;
  level: ViewLevel;
  /** Entree grisee (aria-disabled), non selectionnable. */
  disabled: boolean;
  /** Pourquoi elle est grisee : montre au survol (title). */
  reason?: string;
}
